import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import MenuOptionsBottomSheet from './menuOptionsBottomSheet';
import CartDrawer from './cartDrawer';
import { tapClickProps } from '../utils/tapFix';
import OrderHistory from './orderHistory';
import StaffCallBottomSheet from './staffCallBottomSheet';
import api, { getStoreIdFromToken, getStoreCodeFromToken } from '../utils/api';
import { checkTableOrderExists as checkTableOrderExistsUnion } from '../utils/unionApi';
import { checkTableOrderExists as checkTableOrderExistsOKDC } from '../utils/okdcApi';
import { checkTableOrderExists as checkTableOrderExistsMetacity } from '../utils/metacityApi';
import { checkTableOrderExists as checkTableOrderExistsToss } from '../utils/tossApi';
import { fetchWowposOrderHistory } from '../utils/wowposApi';
import useLayoutMode, { isTabletForced } from '../hooks/useLayoutMode';
import TabletOrderView from './tablet/TabletOrderView';
import { simApi } from '../utils/simApi';
import { convertImagesToCdn, convertMenuImagesToCdn, convertToCdnUrl, prefetchImages } from '../utils/cdnUtils';
import wsService from '../utils/websocket';

// Icon imports - common
import bottomNavImg from '../assets/icons/common/bottomNav.png';
import languageIconImg from '../assets/icons/common/languageicon.png';
import noticeIconImg from '../assets/icons/common/noticeicon.png';
import liveStatusImg from '../assets/icons/common/live_status.png';
import orderHistoryImg from '../assets/icons/common/order_history.png';
import staffCallImg from '../assets/icons/common/staff_call.png';
import navigationBarImg from '../assets/icons/common/navigation_bar.png';

// Icon imports - language
import langKoImg from '../assets/icons/language/ko.png';
import langEnImg from '../assets/icons/language/en.png';
import langCnImg from '../assets/icons/language/cn.png';
import langJaImg from '../assets/icons/language/ja.png';

// Icon imports - review
import liveStatusNaverImg from '../assets/icons/review/live_status_naver.png';

// Icon imports - menutag
import menutagKoreanNew from '../assets/icons/menutag/korean/new.png';
import menutagKoreanPopular from '../assets/icons/menutag/korean/popular.png';
import menutagKoreanRecommend from '../assets/icons/menutag/korean/recommend.png';
import menutagEnglishNew from '../assets/icons/menutag/english/new.png';
import menutagEnglishPopular from '../assets/icons/menutag/english/popular.png';
import menutagEnglishRecommend from '../assets/icons/menutag/english/recommend.png';
import menutagChinaNew from '../assets/icons/menutag/china/new.png';
import menutagChinaPopular from '../assets/icons/menutag/china/popular.png';
import menutagChinaRecommend from '../assets/icons/menutag/china/recommend.png';
import menutagJapanNew from '../assets/icons/menutag/japan/new.png';
import menutagJapanPopular from '../assets/icons/menutag/japan/popular.png';
import menutagJapanRecommend from '../assets/icons/menutag/japan/recommend.png';

const menutagImages = {
  'korean/new': menutagKoreanNew,
  'korean/popular': menutagKoreanPopular,
  'korean/recommend': menutagKoreanRecommend,
  'english/new': menutagEnglishNew,
  'english/popular': menutagEnglishPopular,
  'english/recommend': menutagEnglishRecommend,
  'china/new': menutagChinaNew,
  'china/popular': menutagChinaPopular,
  'china/recommend': menutagChinaRecommend,
  'japan/new': menutagJapanNew,
  'japan/popular': menutagJapanPopular,
  'japan/recommend': menutagJapanRecommend,
};

// tableOrder: 태블릿(테이블오더) 모드. { storeUid, tableCode }
// 값이 있으면 QR 세션을 쓰지 않고 URL 로 매장·테이블을 해석한다 (TableOrderPage 가 주입).
const OrderPage = ({ tableOrder = null }) => {
  // QR 세션 관련 상태
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  // 영업시간 외 차단 화면 ({ openTime, closeTime } | null)
  const [storeClosed, setStoreClosed] = useState(null);
  const [tableNumber, setTableNumber] = useState('');

  // 레이아웃 모드 — 태블릿 UI 는 테이블오더(태블릿) 매장에서만 띄운다.
  // 화면 크기·방향은 보지 않는다. 가로/세로로 갈리면 같은 매장에서 화면이 오락가락하고,
  // 태블릿을 도입하지 않은 매장의 QR 손님이 아이패드 가로로 들어와도 태블릿 UI 가 떴다.
  const [forcedTablet] = useState(() => isTabletForced());
  const { isTablet: isTabletLayout } = useLayoutMode(!!tableOrder || forcedTablet);
  // 태블릿 사이드바의 카테고리 선택. activeTab(모바일 스크롤 스파이 전용)과 반드시 분리한다 —
  // 태블릿에선 categoryHeaderRefs 가 비어 있어 스파이가 activeTab 을 0 으로 되돌리기 때문.
  const [tabletCategoryIndex, setTabletCategoryIndex] = useState(0);

  // 기존 상태들
  const [activeTab, setActiveTab] = useState(0);
  const [, setIsSticky] = useState(false);
  const [highlightStyle, setHighlightStyle] = useState({});
  const [currentSlide, setCurrentSlide] = useState(1);
  const [realIndex, setRealIndex] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [activeUsers, setActiveUsers] = useState(1); // 현재 접속 사용자 수 (기본값 1)
  const [, setWsConnected] = useState(false); // WebSocket 연결 상태
  const prevActiveUsersRef = useRef(1); // 이전 접속자 수를 저장하기 위한 ref
  const [storeInfo, setStoreInfo] = useState({
    storeName: '',
    businessName: '',
    address: '',
    phoneNumber: '',
    businessNumber: '',
    origins: ''
  });
  const [sliderImages, setSliderImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true); // 이미지 로딩 상태 추가
  const extendedImages = sliderImages.length > 0 ? [
    sliderImages[sliderImages.length - 1],
    ...sliderImages,
    sliderImages[0]
  ] : [];

  // 비디오 URL 감지 (mp4/webm/mov/m4v 등)
  const isVideoUrl = (url) => typeof url === 'string' && /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(url);

  // 슬라이드별 video element refs
  const videoRefs = useRef({});
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // 우선순위: 1) 사용자가 직접 선택한 언어 (localStorage)
    //          2) 디바이스/브라우저 언어 자동 감지
    //          3) 한국어 (디폴트)
    const savedLanguage = localStorage.getItem('customerLanguage');
    if (savedLanguage) return savedLanguage;

    const supported = ['ko', 'en', 'ja', 'zh'];
    const langs = navigator.languages || [navigator.language || navigator.userLanguage || ''];
    for (const lang of langs) {
      const code = (lang || '').toLowerCase().split('-')[0]; // ko-KR → ko, zh-TW → zh
      if (supported.includes(code)) return code;
    }
    return 'ko';
  });
  const [isNoticeExpanded, setIsNoticeExpanded] = useState(false);
  const [noticeNeedsExpand, setNoticeNeedsExpand] = useState(false);
  const [noticeSettings, setNoticeSettings] = useState({
    enabled: false,
    texts: {
      ko: '',
      en: '',
      ja: '',
      zh: ''
    }
  });
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
  const [skipOrderHistoryAnimation, setSkipOrderHistoryAnimation] = useState(false);
  const [isStaffCallOpen, setIsStaffCallOpen] = useState(false);
  const [, setCartData] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  // hasReviewOrder는 hasExistingOrder && reviewSettings.isEnabled로 대체됨
  const [toastMessage, setToastMessage] = useState('');
  
  // 룰렛 애니메이션 상태
  const [displayPrice, setDisplayPrice] = useState(0);
  const [prevPrice, setPrevPrice] = useState(0);
  const [priceAnimating, setPriceAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [isPriceIncreasing, setIsPriceIncreasing] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // cart-count 애니메이션 상태
  const [displayCount, setDisplayCount] = useState(0);
  const [prevCount, setPrevCount] = useState(0);
  const [countAnimating, setCountAnimating] = useState(false);
  const [countAnimationKey, setCountAnimationKey] = useState(0);
  const [isCountIncreasing, setIsCountIncreasing] = useState(true);
  const [isCountInitialized, setIsCountInitialized] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isToastVisible, setIsToastVisible] = useState(false);

  // 리뷰 설정 상태 (DB에서 로드)
  const [reviewSettings, setReviewSettings] = useState({
    isEnabled: false,
    menus: []
  });

  // 테이블 주문 여부 상태 (페이지 진입 시 체크)
  const [hasExistingOrder, setHasExistingOrder] = useState(false);

  // 네이버 리뷰 자동감지 - 첫방문 mark 상태
  const [reviewMark, setReviewMark] = useState({ marked: false, benefitName: null, benefitImageUrl: null, placeId: null });

  // 공유 모드 (관리자가 naver.me 생성 위해 ?share=qrCode 로 접근)
  const [shareMode, setShareMode] = useState(null); // null | { qrCode }

  // 쿠키에서 값 읽는 헬퍼 함수
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // 네이버 인앱 브라우저 → 크롬 자동 dispatch
  // 손님이 naver.me QR 을 찍으면 네이버 앱 인앱 브라우저가 열리는데,
  // 이 때 같은 URL 을 크롬으로도 열어서 두 앱이 동시에 열리도록 함.
  useEffect(() => {
    const ua = navigator.userAgent;
    if (!/naver/i.test(ua)) return; // 네이버 인앱 아니면 skip
    if (/iphone|ipad|ipod/i.test(ua)) return; // iOS는 intent:// 무시 → dispatch 안 함

    // 이미 dispatch 했는지 sessionStorage 로 체크 (무한 루프 방지)
    if (sessionStorage.getItem('chromeDispatched') === 'true') return;
    sessionStorage.setItem('chromeDispatched', 'true');

    const ourPageUrl = window.location.href;
    const chromeIntent = 'intent://' + ourPageUrl.replace(/^https?:\/\//, '')
      + '#Intent;scheme=https;package=com.android.chrome;end';

    setTimeout(() => {
      try {
        // 1순위: iframe 방식 (네이버 공식 권장 — 인앱 브라우저 유지하면서 크롬 외부 실행)
        const frame = document.createElement('iframe');
        frame.style.cssText = 'display:none;width:0;height:0;border:0';
        frame.src = chromeIntent;
        document.body.appendChild(frame);
      } catch (e) {
        // 2순위: location.href fallback
        try { window.location.href = chromeIntent; } catch (e2) {}
      }
    }, 300);
  }, []);

  // 공유 모드: 관리자가 ?share=qrCode 로 접근 시 URL 바를 /order/{qrCode} 로 표시
  // → 네이버 앱에서 공유 버튼 누르면 naver.me 가 깨끗한 QR URL 로 생성됨
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareQr = urlParams.get('share');
    if (shareQr) {
      window.history.replaceState(null, '', `/order/${shareQr}`);
      setShareMode({ qrCode: shareQr });
    }
  }, []);

  // QR 세션 검증 및 초기화
  // ※ deps 를 [] 로 두는 이유: tableOrder 는 라우터 파라미터로 매 렌더 새 객체가 만들어져
  //    deps 에 넣으면 setSessionData → 재렌더 → 재조회 무한 루프가 된다. 진입 1회면 충분하다.
  useEffect(() => {
    // 태블릿(테이블오더) 모드 — 세션 없음.
    // /tableorder/<store_uid>/<table_code> 를 서버가 해석해 세션과 같은 shape 을 돌려준다.
    // 쿠키 저장도, URL 정리(replaceState)도 하지 않는다 —
    // 주소가 바뀌면 태블릿 앱이 기억한 시작 주소가 틀어진다.
    if (tableOrder) {
      let cancelled = false;
      let timer = null;

      const resolveTable = async () => {
        try {
          const url = `/api/tableorder/resolve/${encodeURIComponent(tableOrder.storeUid)}/${encodeURIComponent(tableOrder.tableCode)}`;
          const response = await fetch(url);
          const data = await response.json().catch(() => ({}));
          if (cancelled) return;

          if (data.success) {
            setSessionError(null);
            setSessionData(data.store);
            setTableNumber(data.store.table_name);
            console.log('[테이블오더 진입]', data.store);
            return;
          }

          // 5xx 는 일시 장애로 보고 재시도, 4xx 는 주소 자체가 잘못된 것이라 그대로 노출
          if (response.status >= 500) {
            timer = setTimeout(resolveTable, 5000);
            return;
          }
          setSessionError(data.message || '등록되지 않은 태블릿 주소입니다.');
        } catch (error) {
          if (cancelled) return;
          // 태블릿은 손님이 새로고침할 수 없다 → 막다른 화면 대신 계속 재시도
          console.error('[테이블오더] 매장 정보 조회 실패, 5초 후 재시도', error);
          timer = setTimeout(resolveTable, 5000);
        }
      };

      resolveTable();
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
      };
    }

    // 공유 모드면 세션 검증 건너뜀 (?share 또는 replaceState 후 /order/{qrCode} 패턴 둘 다 체크)
    const shareQr = new URLSearchParams(window.location.search).get('share');
    const isShareModePath = /^\/order\/[A-Za-z0-9_-]+$/.test(window.location.pathname);
    if (shareQr || isShareModePath) {
      console.log('[공유 모드] 세션 검증 건너뜀');
      return;
    }

    // 네이버 인앱(Android) → 세션 검증 건너뜀 (qr.js에서 이미 크롬으로 handoff 처리됨)
    // iOS 네이버 인앱은 dispatch 안 하고 인앱에서 직접 렌더하므로 세션 검증 실행해야 함
    // (안 하면 sessionData 가 null 로 남아 흰 화면)
    const naverUa = navigator.userAgent;
    if (/naver/i.test(naverUa) && !/iphone|ipad|ipod/i.test(naverUa)) {
      console.log('[네이버 인앱-Android] 세션 검증 건너뜀 (크롬 dispatch 대기)');
      return;
    }

    const verifySession = async () => {
      try {
        // 1. URL에서 session 파라미터 읽기 (기존 방식 지원)
        const urlParams = new URLSearchParams(window.location.search);
        let session = urlParams.get('session');
        const sessionFromUrl = !!session;

        // 2. URL에 없으면 쿠키에서 읽기 (새로운 방식)
        if (!session) {
          session = getCookie('qr_session');
        }

        // 시뮬 진입 표시가 있으면 세션 만료/없음 시 자동으로 /simulation 으로 재진입
        const isSim = (() => {
          try { return sessionStorage.getItem('isSimulation') === 'true'; } catch (_) { return false; }
        })();

        if (!session) {
          if (isSim) { window.location.replace('/simulation'); return; }
          setSessionError('세션 토큰이 없습니다. QR 코드를 다시 스캔해주세요.');
          console.error('[세션 에러] Session token not found in URL or cookie');
          return;
        }

        setSessionToken(session);

        // 세션 검증 API 호출
        const response = await fetch(`/order/session/verify?session=${session}`);
        const data = await response.json();

        if (!data.success) {
          if (isSim) { window.location.replace('/simulation'); return; }
          setSessionError(data.expired ? '세션이 만료되었습니다. QR 코드를 다시 스캔해주세요.' : '유효하지 않은 세션입니다.');
          console.error('[세션 검증 실패]', data.error);
          return;
        }

        // 시뮬 매장(36) self-heal — PWA(홈화면) 재실행 시 sessionStorage 가 비어
        // isSimulation 플래그가 사라지면 주문이 실 Union POS 로 새서 실패한다.
        // 시뮬 여부는 서버가 판정해 내려준다(session/verify 의 isSimulation).
        // 매장 id 하드코딩은 서버마다 그 id 의 의미가 달라 오판한다
        // — 테스트의 36 은 시뮬 매장이지만 프로덕션의 36 은 실매장이다.
        // ※ 영업시간 차단 검사보다 먼저 해야 한다. 플래그가 비어 있으면 시뮬 매장이
        //   "주문 시간이 아닙니다" 화면에 갇힌다.
        // ※ store 36 을 시뮬로 단정하지는 않는다 — 프로덕션의 36 번은 실매장(스시이안앤
        //   전북익산점)이라, id 만으로 차단을 면제하면 그 매장 영업시간 제한이 풀린다.
        if (data.isSimulation === true) {
          try { sessionStorage.setItem('isSimulation', 'true'); } catch (_) {}
        }

        // 영업시간 외 재진입 차단 (QR 스캔은 서버가 이미 막지만, 쿠키 세션 직접 진입은 여기로 들어온다)
        // 판정은 서버 KST 기준이며 시뮬레이션은 예외.
        //
        // isSim 은 위(sessionStorage 읽기)에서 이미 확정된 값이라, 바로 앞의
        // self-heal 이 sessionStorage 에 써도 갱신되지 않는다. 그래서 이번 응답의
        // data.isSimulation 을 함께 본다 — PWA 첫 실행처럼 플래그가 비어 있는
        // 상태에서 시뮬 매장이 차단 화면에 갇히는 것을 막는다.
        if (data.isOpen === false && !isSim && data.isSimulation !== true) {
          setStoreClosed({ openTime: data.openTime, closeTime: data.closeTime });
          return;
        }

        // 세션 데이터 저장
        setSessionData(data.store);
        setTableNumber(data.store.table_name);
        console.log('[세션 검증 성공]', {
          store_id: data.store.store_id,
          store_name: data.store.store_name,
          table_name: data.store.table_name,
          table_code: data.store.table_code,
          session_ttl: data.session_ttl
        });

        // ★ URL session → 쿠키 복사 + URL 정리
        // (autoChromeDispatch 가 이미 원본 URL 캡처했으므로 이제 정리해도 안전)
        if (sessionFromUrl) {
          document.cookie = `qr_session=${session}; path=/; max-age=1800; SameSite=Lax`;
          window.history.replaceState(null, '', '/order');
        }

      } catch (error) {
        setSessionError('세션 검증 중 오류가 발생했습니다.');
        console.error('[세션 검증 에러]', error);
      }
    };

    verifySession();
  }, []);

  // 언어 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('customerLanguage', selectedLanguage);
    // 선택 언어를 <html lang> 에 동기화 → 브라우저 자동번역 추가 차단 (특히 iOS Safari)
    try { document.documentElement.lang = selectedLanguage; } catch (_) {}
  }, [selectedLanguage]);

  // 리뷰 설정 로드 함수 (useCallback으로 컴포넌트 레벨에 정의)
  const loadReviewSettings = useCallback(async () => {
    // 세션 데이터가 없으면 대기
    if (!sessionData) {
      return;
    }

    try {
      const storeId = sessionData.store_id;
      console.log('[리뷰 설정 로드] storeId:', storeId);

      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      // 리뷰 설정 로드 (BOOLEAN)
      const settingsResponse = await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/review-settings`);
      const settingsData = await settingsResponse.json();

      if (settingsData.success) {
        const naverOn = settingsData.isEnabled || false;
        const kakaoOn = settingsData.kakaoEnabled || false;
        const isEnabled = naverOn || kakaoOn; // 네이버/카카오 중 하나라도 켜지면 리뷰 흐름 활성
        const channel = settingsData.channel || (kakaoOn ? 'kakao' : (naverOn ? 'naver' : null));
        const kakaoPlaceId = settingsData.kakaoPlaceId || null;
        const note = settingsData.note || '';
        const base = { isEnabled, channel, kakaoPlaceId, note };
        console.log('[리뷰 설정 로드] ✅ 성공:', { isEnabled, channel });

        // 리뷰가 활성화된 경우에만 메뉴 로드
        if (isEnabled) {
          // 수동 등록된 리뷰 혜택 메뉴 로드 (모드 무관 — review_benefit_menus 테이블)
          const benefitResponse = await fetch(`${API_BASE_URL}/api/stores/${storeId}/review-benefit-menus`);
          const benefitData = await benefitResponse.json();

          if (benefitData.success && Array.isArray(benefitData.menus)) {
            // 활성 + 미삭제 + 미품절 메뉴만 노출
            const availableMenus = benefitData.menus.filter(
              m => m.is_enabled !== false && !m.is_deleted && !m.is_soldout
            );

            const formattedMenus = availableMenus.map(menu => ({
              id: menu.menu_id || menu.menu_key,
              name: menu.name,
              menu_code: menu.menu_key,
              prod_cd: menu.menu_key,
              price: menu.price,
              basePrice: menu.price,
              // 정규화 지점에서 CDN 으로 바꾼다 — 렌더하는 곳(reviewBenefits 등)이
              // 여러 군데라 여기서 한 번에 처리하는 편이 빠뜨릴 위험이 적다.
              image_url: convertToCdnUrl(menu.image_url),
              is_soldout: menu.is_soldout,
              is_taxable: 'Y',
              // 사은품 명시 표식. id 값 비교로 판별하면 정규 메뉴 id 와 겹칠 수 있다
              // (review_benefit_items.id 와 메뉴 id 가 각각 SERIAL 이라 충돌 가능).
              is_review_benefit: true
            }));

            console.log('[리뷰 메뉴 로드] ✅ 등록 혜택 메뉴:', formattedMenus.length, '개');
            setReviewSettings({ ...base, menus: formattedMenus });
          } else {
            setReviewSettings({ ...base, menus: [] });
          }
        } else {
          // 리뷰가 비활성화된 경우
          setReviewSettings({ ...base, menus: [] });
        }
      } else {
        console.log('[리뷰 설정 로드] DB에 데이터 없음, 기본값 사용');
        setReviewSettings({ isEnabled: false, channel: null, kakaoPlaceId: null, note: '', menus: [] });
      }
    } catch (error) {
      console.error('[리뷰 설정 로드] ❌ 실패:', error);
      setReviewSettings({ isEnabled: false, menus: [] });
    }
  }, [sessionData]);

  // 매장 정보 및 슬라이더 이미지 로드
  useEffect(() => {
    const loadStoreInfo = async () => {
      // 세션 데이터가 없으면 대기
      if (!sessionData) {
        return;
      }

      try {
        const storeId = sessionData.store_id;
        if (!storeId) {
          console.error('Store ID not found');
          return;
        }

        // DB에서 매장 정보 로드 (Redis 캐싱됨)
        const response = await api.getStoreInfo(storeId);
        console.log('[매장 정보 로드] API 응답:', response);

        // API 응답 구조: response.store (response.storeInfo가 아님)
        const storeData = response.storeInfo || response.store;

        if (response.success && storeData) {
          setStoreInfo({
            storeName: storeData.store_name || '',
            businessName: storeData.business_name || '',
            address: storeData.business_address || '',
            phoneNumber: storeData.business_phone || '',
            businessNumber: storeData.business_number || '',
            origins: storeData.ingredient_info || ''
          });
          console.log('[매장 정보 로드] storeName:', storeData.store_name);
        }
      } catch (error) {
        console.error('Failed to load store info:', error);
      }
    };

    const loadSliderImages = async () => {
      // 세션 데이터가 없으면 대기
      if (!sessionData) {
        return;
      }

      try {
        setIsLoadingImages(true); // 로딩 시작
        const storeId = sessionData.store_id;
        console.log('[배너 이미지 로드] storeId:', storeId);
        const response = await fetch(`/api/banners/${storeId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.images && data.images.length > 0) {
            // CDN URL로 변환하여 저장
            const cdnImages = convertImagesToCdn(data.images);
            const imageUrls = cdnImages.map(img => img.image_url);
            setSliderImages(imageUrls);

            // 중요 이미지 프리페치 (처음 3개)
            prefetchImages(imageUrls.slice(0, 3));

            // console.log('배너 이미지 로드 완료:', imageUrls.length, '개');
          } else {
            // console.log('배너 이미지가 없습니다.');
            // 기본 이미지 설정 (선택사항)
            setSliderImages([]);
          }
        }
        setIsLoadingImages(false); // 로딩 완료
      } catch (error) {
        console.error('Failed to load banner images from DB:', error);
        // 에러 시 빈 배열 설정
        setSliderImages([]);
        setIsLoadingImages(false); // 오류 시에도 로딩 완료
      }
    };

    // 공지사항 데이터 로드 (DB에서)
    const loadNoticeData = async () => {
      // 세션 데이터가 없으면 대기
      if (!sessionData) {
        return;
      }

      try {
        const storeId = sessionData.store_id;
        console.log('[공지사항 로드] storeId:', storeId);
        const response = await fetch(`/api/notices/${storeId}`);
        const data = await response.json();

        if (data.success && data.notice) {
          setNoticeSettings({
            enabled: data.notice.enabled,
            texts: data.notice.texts
          });
        }
      } catch (error) {
        console.error('Failed to load notice from DB:', error);
      }
    };

    const checkExistingOrder = async () => {
      // 세션 데이터가 없으면 대기
      if (!sessionData) {
        return;
      }

      try {
        const storeId = sessionData.store_id;
        const tableCode = sessionData.table_code;
        const posType = sessionData.pos_type;
        // 매장 36 hardcode 제거 — 영업 시연용은 sessionStorage('isSimulation') 으로 판단
        // (SimulationTablePage 진입 시 set, 일반 손님 QR 스캔은 sessionStorage 없음)
        const isSim = sessionStorage.getItem('isSimulation') === 'true';

        console.log('[테이블 주문 체크] storeId:', storeId, 'tableCode:', tableCode, 'posType:', posType, 'isSim:', isSim);

        let orderCheck;
        if (isSim) {
          // 시뮬 매장 — Redis sim_orders 기반 (본 union/okdc API 분기 제거됨)
          orderCheck = await simApi.checkTableOrderExists();
        } else if (posType === 'okdc') {
          orderCheck = await checkTableOrderExistsOKDC(storeId, tableCode);
        } else if (posType === 'wowpos') {
          // 와우포스: PC 메모리 _tableOrders 누적 주문 확인
          try {
            const wp = await fetchWowposOrderHistory(storeId, tableCode);
            const has = Array.isArray(wp.orders) && wp.orders.length > 0;
            orderCheck = { hasOrder: has };
          } catch (wpErr) {
            console.warn('[테이블 주문 체크] WowPos 조회 실패 — 첫 주문 가정:', wpErr.message);
            orderCheck = { hasOrder: false };
          }
        } else if (posType === 'metacity') {
          // 메타시티: 8060 현주문내역 조회 (기존엔 분기가 없어 union API 로 빠져 항상 false 였음)
          orderCheck = await checkTableOrderExistsMetacity(storeId, tableCode);
        } else if (posType === 'toss') {
          // 토스: 우리 DB(toss_orders) 기준. POS 에 물으면 SDK rate limit 을 쓴다.
          orderCheck = await checkTableOrderExistsToss(storeId, tableCode);
        } else {
          orderCheck = await checkTableOrderExistsUnion(tableCode);
        }

        console.log('[테이블 주문 체크] 결과:', orderCheck);
        setHasExistingOrder(orderCheck.hasOrder || false);
      } catch (error) {
        console.error('[테이블 주문 체크] ❌ 실패:', error);
        setHasExistingOrder(false);
      }
    };

    // 네이버 리뷰 첫방문 mark 조회
    // 태블릿은 QR 재스캔 기반 리뷰 유도(카운트다운)를 쓰지 않는다 → 조회 자체를 건너뛴다.
    // (화면에 리뷰 QR 을 띄워 손님 폰으로 넘기는 방식은 별도 UI 로 추후 제작)
    const checkReviewMark = async () => {
      if (!sessionData || tableOrder) return;
      try {
        const storeId = sessionData.store_id;
        const tableCode = sessionData.table_code;
        const isSim = sessionStorage.getItem('isSimulation') === 'true';
        const url = isSim
          ? '/api/simulation/review/first-visit-status'
          : `/api/review/first-visit-status?storeId=${storeId}&tableCode=${encodeURIComponent(tableCode)}`;
        const response = await fetch(url);
        const data = await response.json();
        console.log('[리뷰 mark 체크] 결과:', data);
        if (data.marked) {
          setReviewMark({
            marked: true,
            benefitName: data.benefitName || null,
            benefitImageUrl: data.benefitImageUrl || null,
            placeId: data.placeId || null,
            channel: data.channel || 'naver',
            kakaoPlaceId: data.kakaoPlaceId || null,
          });
        } else {
          setReviewMark({ marked: false, benefitName: null, benefitImageUrl: null, placeId: null, channel: 'naver', kakaoPlaceId: null });
        }
      } catch (error) {
        console.error('[리뷰 mark 체크] 실패:', error);
        setReviewMark({ marked: false, benefitName: null, benefitImageUrl: null, placeId: null, channel: 'naver', kakaoPlaceId: null });
      }
    };

    // 초기 로드
    loadStoreInfo();
    loadSliderImages();
    loadNoticeData();
    loadReviewSettings();
    checkExistingOrder();
    checkReviewMark();
    
    // storage 이벤트 리스너 추가
    const handleStorageChange = (e) => {
      if (e.key === 'storeSettings') {
        loadStoreInfo();
      } else if (e.key === 'bannersUpdated') {
        // 관리자가 배너를 업데이트했을 때 DB에서 다시 로드
        loadSliderImages();
      } else if (e.key === 'noticeUpdated') {
        // 관리자가 공지사항을 업데이트했을 때
        loadNoticeData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [sessionData]);

  // 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // 10초마다 업데이트
    
    return () => clearInterval(timer);
  }, []);
  
  // 메뉴 이용 가능 시간 확인 함수
  const calculateTimeRemaining = (item) => {
    // 판매시간이 설정되지 않았거나 비활성화된 경우
    if (!item.is_sales_time && !item.openTime) return null;

    // 새 필드명과 기존 필드명 모두 확인
    const startTime = item.start_time || item.openTime;
    const endTime = item.end_time || item.closeTime;

    if (!startTime || !endTime) return null;
    
    const now = currentTime;
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;
    
    // 오픈/클로즈 시간을 분 단위로 변환
    const [openHour, openMinute] = startTime.split(':').map(Number);
    const [closeHour, closeMinute] = endTime.split(':').map(Number);
    let openTimeInMinutes = openHour * 60 + openMinute;
    let closeTimeInMinutes = closeHour * 60 + closeMinute;
    
    // 자정을 넘어가는 경우 처리 (예: 22:00 ~ 02:00)
    if (closeTimeInMinutes < openTimeInMinutes) {
      // 클로즈 시간이 다음날인 경우
      if (currentTimeInMinutes < closeTimeInMinutes) {
        // 현재가 자정 이후 클로즈 시간 전 (새벽 시간대)
        return null; // 주문 가능
      } else if (currentTimeInMinutes >= openTimeInMinutes) {
        // 현재가 오픈 시간 이후 (밤 시간대)
        return null; // 주문 가능
      }
    } else {
      // 일반적인 경우 (예: 10:00 ~ 22:00)
      if (currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes < closeTimeInMinutes) {
        return null; // 주문 가능
      }
    }
    
    // 오픈까지 남은 시간 계산
    let minutesUntilOpen;
    
    if (currentTimeInMinutes < openTimeInMinutes) {
      // 같은 날 내에서 오픈 전
      minutesUntilOpen = openTimeInMinutes - currentTimeInMinutes;
    } else {
      // 현재가 클로즈 시간 이후, 다음 날 오픈까지
      minutesUntilOpen = (1440 - currentTimeInMinutes) + openTimeInMinutes;
    }
    
    const hoursUntilOpen = Math.floor(minutesUntilOpen / 60);
    const remainingMinutes = minutesUntilOpen % 60;
    
    if (hoursUntilOpen >= 1) {
      // 1시간 이상 남았을 때
      return {
        type: 'beforeOpen',
        hours: hoursUntilOpen,
        minutes: null
      };
    } else {
      // 1시간 미만 남았을 때
      return {
        type: 'beforeOpen',
        hours: null,
        minutes: remainingMinutes
      };
    }
  };
  
  // 토스트 메시지 표시 함수
  const showToast = (message) => {
    // 이전 토스트가 있으면 먼저 숨기기
    setIsToastVisible(false);
    
    // 새 토스트 즉시 표시
    setTimeout(() => {
      setToastMessage(message);
      setIsToastVisible(true);
      
      // 2초 후 페이드아웃 시작, 애니메이션 완료 후 실제로 숨기기
      setTimeout(() => {
        // 페이드아웃 애니메이션이 끝나면 완전히 숨기기 (0.3초 후)
        setTimeout(() => {
          setIsToastVisible(false);
        }, 300);
      }, 1700); // 1.7초 후 페이드아웃 시작
    }, 10);
  };

  // 시뮬레이션: 테이블 배지 클릭 시 주문내역 초기화 (POS 화면 새로고침과 동일 효과)
  const handleTableBadgeReset = async () => {
    const isSim = (() => { try { return sessionStorage.getItem('isSimulation') === 'true'; } catch (_) { return false; } })();
    if (!isSim) return;
    try {
      await simApi.reset();
    } catch (_) { /* 무시 */ }
    // 초기화 후 새로고침까지 진행 (깨끗한 상태로 재시작)
    window.location.reload();
  };



  // Refs
  const tabsRef = useRef(null);
  const tabRefs = useRef([]);
  const sectionRefs = useRef([]);
  const categoryHeaderRefs = useRef([]);
  const tabContainerRef = useRef(null);
  const autoPlayRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const scrollEndTimeoutRef = useRef(null);
  const noticeRef = useRef(null);
  const noticeContentRef = useRef(null);
  
  // categories 선언은 위에서 이미 처리됨
  
  const languages = [
    { code: 'zh', name: 'CN', flag: langCnImg },
    { code: 'ja', name: 'JA', flag: langJaImg },
    { code: 'en', name: 'EN', flag: langEnImg },
    { code: 'ko', name: 'KO', flag: langKoImg }
  ];
  
  // UI 텍스트 번역
  const translations = {
    ko: {
      callStaff: '직원호출',
      orderingTogether: '명이 같이 음식을 담고 있어요!',
      orderingAlone: '명이 음식을 담고 있어요!',
      orders: '주문내역',
      tablePrefix: '',
      tableSuffix: ' 테이블',
      tags: {
        popular: '인기',
        recommend: '추천',
        new: '신메뉴'
      },
      businessInfo: '사업자 정보',
      originInfo: '원산지 정보',
      cart: '장바구니',
      inCart: ' in cart'
    },
    en: {
      callStaff: 'Call Staff',
      orderingTogether: ' people ordering together!',
      orderingAlone: ' person ordering!',
      orders: 'Orders',
      tablePrefix: 'Table ',
      tableSuffix: '',
      tags: {
        popular: 'Popular',
        recommend: 'Recommend',
        new: 'New'
      },
      businessInfo: 'Business Info',
      originInfo: 'Country of Origin',
      cart: 'cart',
      inCart: ' in cart'
    },
    zh: {
      callStaff: '呼叫',
      orderingTogether: '人正在一起点餐！',
      orderingAlone: '人正在点餐！',
      orders: '订单记录',
      tablePrefix: '',
      tableSuffix: '号桌',
      tags: {
        popular: '人气',
        recommend: '推荐',
        new: '新品'
      },
      businessInfo: '商户信息',
      originInfo: '原产地信息',
      cart: '查看购物车',
      inCart: ' in cart'
    },
    ja: {
      callStaff: '呼出',
      orderingTogether: '名で一緒に注文中です！',
      orderingAlone: '名が注文中です！',
      orders: '注文履歴',
      tablePrefix: 'テーブル ',
      tableSuffix: '',
      tags: {
        popular: '人気',
        recommend: 'おすすめ',
        new: '新メニュー'
      },
      businessInfo: '事業者情報',
      originInfo: '原産地情報',
      cart: 'カート',
      inCart: ' in cart'
    }
  };
  
  const t = translations[selectedLanguage] || translations.ko;

  // 다국어 메뉴 데이터 상태 관리
  const [menuData, setMenuData] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [translatedMenus, setTranslatedMenus] = useState({});
  const [translatedCategories, setTranslatedCategories] = useState({});
  const [categoryCodeMapping, setCategoryCodeMapping] = useState({});
  const [categoryTranslations, setCategoryTranslations] = useState({}); // 카테고리 번역 정보

  // 메뉴 검색
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const closeSearch = () => { setIsSearchOpen(false); setSearchQuery(''); };
  const searchPlaceholder = {
    ko: '메뉴 검색',
    en: 'Search menu',
    ja: 'メニュー検索',
    zh: '搜索菜单',
  }[selectedLanguage] || '메뉴 검색';
  const searchEmptyText = {
    ko: '메뉴 이름을 입력하세요',
    en: 'Enter menu name',
    ja: 'メニュー名を入力',
    zh: '请输入菜单名称',
  }[selectedLanguage] || '메뉴 이름을 입력하세요';
  const searchNoResultsText = {
    ko: '검색 결과가 없습니다',
    en: 'No results found',
    ja: '検索結果がありません',
    zh: '没有搜索结果',
  }[selectedLanguage] || '검색 결과가 없습니다';

  // 검색 결과 (카테고리별 그룹화) — { categoryName: [items...] }
  const getSearchResults = () => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return null;
    const source = menuData || defaultMenuData || {};
    const results = {};
    (categoryOrder.length ? categoryOrder : Object.keys(source)).forEach(category => {
      const items = (source[category] || []).filter(item => {
        if (item.is_hidden) return false;
        const fields = [
          item.name, item.name_ko, item.name_en, item.name_ja, item.name_zh,
          item.description, item.description_ko, item.description_en, item.description_ja, item.description_zh,
          translatedMenus[item.itemCode]?.name,
          translatedMenus[item.itemCode]?.description,
        ];
        return fields.some(f => f && String(f).toLowerCase().includes(q));
      });
      if (items.length > 0) results[category] = items;
    });
    return results;
  };

  // DB에서 번역된 메뉴 데이터 가져오기
  const fetchTranslatedData = async (language, codeMappings) => {
    try {
      const storeId = sessionData?.store_id; // 세션의 매장 ID 사용 (QR 손님은 토큰이 없음)
      if (!storeId) return;

      // 카테고리 번역 데이터 가져오기
      const categoryResponse = await fetch(`/api/translations/categories/${storeId}/${language}`);
      const categoryData = await categoryResponse.json();

      if (categoryData.success) {
        const catTranslations = {};
        categoryData.categories.forEach(cat => {
          // category_code로 받은 번역을 category_name으로 매핑
          const categoryName = Object.keys(codeMappings).find(
            name => codeMappings[name] === cat.category_code
          );
          if (categoryName) {
            catTranslations[categoryName] = cat.name;
          }
        });
        setTranslatedCategories(catTranslations);
      }

      // 메뉴 번역 데이터 가져오기
      const menuResponse = await fetch(`/api/translations/menus/${storeId}/${language}`);
      const menuData = await menuResponse.json();

      if (menuData.success) {
        const menuTranslations = {};
        menuData.menus.forEach(menu => {
          menuTranslations[menu.item_code] = {
            name: menu.name,
            description: menu.description !== null && menu.description !== undefined ? menu.description : ''
          };
        });
        setTranslatedMenus(menuTranslations);
      }
    } catch (error) {
      console.error('Failed to fetch translated data:', error);
    }
  };

  // 언어 변경 시 번역 데이터 가져오기
  useEffect(() => {
    if (Object.keys(categoryCodeMapping).length > 0) {
      fetchTranslatedData(selectedLanguage, categoryCodeMapping);
    }
  }, [selectedLanguage, categoryCodeMapping]);
  
  useEffect(() => {
    // 컨포넌트 마운트 시 Store 정보 로깅
    console.log('========== 손님 페이지 Store 정보 ==========');
    console.log('현재 Store ID:', getStoreIdFromToken() || '비로그인 상태');
    console.log('현재 Store Code:', getStoreCodeFromToken() || '비로그인 상태');
    console.log('기본 Store ID:', 1);
    console.log('==========================================');

    const loadMenuData = async () => {
      try {
        // 세션 데이터가 없으면 대기
        if (!sessionData || !sessionData.store_id) {
          console.log('[메뉴 데이터 로드] 세션 데이터 대기 중...');
          return;
        }

        // DB에서 메뉴 데이터 가져오기
        const storeId = sessionData.store_id;
        console.log('[메뉴 데이터 로드] 시작... Store ID:', storeId);
        const response = await api.getMenus(storeId);
        console.log('[메뉴 데이터 로드] 응답 받음:', response.success ? '성공' : '실패');
        // console.log('API Response:', response);

        if (response.success && response.categories) {
          // DB 데이터를 기존 형식으로 변환
          const dbMenuData = {};
          const dbCategoryOrder = []; // 카테고리 순서 저장
          const dbCategoryCodeMapping = {}; // 카테고리 이름 -> 코드 매핑

          response.categories.filter(cat => !cat.is_hidden).forEach(category => {
            dbCategoryOrder.push(category.category_name); // 순서대로 카테고리 이름 저장
            dbCategoryCodeMapping[category.category_name] = category.category_code; // 카테고리 이름 -> 코드 매핑
            dbMenuData[category.category_name] = category.menus.filter(m => !m.is_hidden).map(menu => ({
              ...menu,
              name: menu.menu_name,
              // 다국어 지원을 위한 이름 필드 추가
              name_ko: menu.name_ko || menu.menu_name,
              name_en: menu.name_en || menu.menu_name,
              name_ja: menu.name_ja || menu.menu_name,
              name_zh: menu.name_zh || menu.menu_name,
              // 다국어 지원을 위한 설명 필드 추가
              description_ko: menu.description_ko || menu.description || '',
              description_en: menu.description_en || menu.description || '',
              description_ja: menu.description_ja || menu.description || '',
              description_zh: menu.description_zh || menu.description || '',
              basePrice: menu.price,
              itemCode: menu.menu_code,  // menu_code를 itemCode로 매핑
              image: menu.image_url || null, // image_url 추가
              // DB 데이터를 기존 형식에 맞게 매핑 - 옵션에 itemCode 포함
              // OKDC: items 배열, Union POS: choices 배열
              options: (menu.options || []).map(optionGroup => ({
                ...optionGroup,
                choices: (optionGroup.choices || optionGroup.items || []).map(choice => ({
                  ...choice,
                  itemCode: choice.itemCode // option_code를 itemCode로 매핑
                }))
              })),
              menuType: menu.menu_type,  // menu_type을 menuType으로 매핑
              is_popular: menu.is_popular || false,
              is_recommend: menu.is_recommend || false,
              is_new: menu.is_new || false,
              is_most_ordered: menu.is_most_ordered || false
            }));
          });
          // CDN URL로 변환
          const cdnMenuData = convertMenuImagesToCdn(dbMenuData);

          // console.log('Converted menu data with CDN:', cdnMenuData);
          // console.log('Category order from DB:', dbCategoryOrder);
          // console.log('Category code mapping:', dbCategoryCodeMapping);

          // 카테고리 번역 정보를 먼저 로드 (깜빡임 방지)
          try {
            const storeId = sessionData.store_id;
            const languages = ['ko', 'en', 'ja', 'zh'];
            const translations = {};

            // 병렬로 모든 언어 번역 로드 (더 빠름)
            const translationPromises = languages.map(async (language) => {
              try {
                const translationResponse = await fetch(`/api/translations/categories/${storeId}/${language}`);
                const translationData = await translationResponse.json();

                if (translationData.success) {
                  return { language, categories: translationData.categories };
                }
              } catch (error) {
                console.error(`[카테고리 번역] ${language} 로드 실패:`, error);
              }
              return null;
            });

            const results = await Promise.all(translationPromises);

            results.forEach(result => {
              if (result) {
                result.categories.forEach(cat => {
                  if (!translations[cat.category_code]) {
                    translations[cat.category_code] = {};
                  }
                  translations[cat.category_code][result.language] = cat.name;
                });
              }
            });

            setCategoryTranslations(translations);
            console.log('[카테고리 번역] API에서 로드:', translations);
          } catch (error) {
            console.error('[카테고리 번역] 로드 실패:', error);
          }

          // 번역 로드 후 메뉴 데이터 설정 (깜빡임 방지)
          setMenuData(cdnMenuData);
          setCategoryOrder(dbCategoryOrder); // 카테고리 순서 저장
          setCategoryCodeMapping(dbCategoryCodeMapping); // 카테고리 코드 매핑 저장

          // 메뉴 이미지 프리페치 (처음 10개) - 구형 브라우저 호환
          const allMenus = [];
          for (let i = 0; i < cdnMenuData.length; i++) {
            const category = cdnMenuData[i];
            if (category.menus && category.menus.length > 0) {
              for (let j = 0; j < category.menus.length; j++) {
                allMenus.push(category.menus[j]);
              }
            }
          }
          const menuImages = [];
          for (let i = 0; i < Math.min(allMenus.length, 10); i++) {
            if (allMenus[i].image_url) {
              menuImages.push(allMenus[i].image_url);
            }
          }
          prefetchImages(menuImages);
        } else {
          // DB 연결 실패시 localStorage 폴백
          const storedMenuData = localStorage.getItem('menuData');

          if (storedMenuData) {
            setMenuData(JSON.parse(storedMenuData));
          } else {
            // localStorage에도 없으면 JSON 파일에서 로드
            const response = await fetch('/defaultMenuData.json');
            if (response.ok) {
              const defaultData = await response.json();
              setMenuData(defaultData.menuData);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load menu data from DB:', error);
        // DB 실패시 localStorage 사용
        const storedMenuData = localStorage.getItem('menuData');
        if (storedMenuData) {
          setMenuData(JSON.parse(storedMenuData));
        } else {
          setMenuData(defaultMenuData);
        }
      }
    };
    
    loadMenuData();

    // storage 이벤트 리스너 추가 (다른 탭에서 변경 시)
    const handleStorageChange = () => {
      loadMenuData();
    };

    window.addEventListener('storage', handleStorageChange);

    // 같은 탭에서 메뉴 데이터 업데이트 시 리스너
    const handleMenuDataUpdate = (event) => {
      loadMenuData();
    };
    window.addEventListener('menuDataUpdated', handleMenuDataUpdate);

    // 같은 탭에서 카테고리 번역 업데이트 시 리스너
    const handleCategoryTranslationUpdate = (event) => {
      console.log('[카테고리 번역 업데이트] 같은 탭에서 감지!');
      loadMenuData();
    };
    window.addEventListener('categoryTranslationsUpdated', handleCategoryTranslationUpdate);

    // 페이지가 포커스를 받을 때 메뉴 데이터 새로고침 (카테고리 순서 업데이트 반영)
    const handleFocus = () => {
      // console.log('Page received focus, reloading menu data...');
      loadMenuData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('menuDataUpdated', handleMenuDataUpdate);
      window.removeEventListener('categoryTranslationsUpdated', handleCategoryTranslationUpdate);
      window.removeEventListener('focus', handleFocus);
    };
  }, [sessionData]);

  // WebSocket 초기화를 위한 별도 useEffect
  useEffect(() => {
    console.log('[OrderPage] WebSocket 초기화 useEffect 실행');
    // storeId를 비동기로 가져와야 함

    // cleanup을 위해 콜백 함수들을 바깥 스코프에 선언
    let cartUpdateHandler = null;

    const initWebSocket = async () => {
      console.log('[OrderPage] initWebSocket 시작');

      // 세션 데이터가 없으면 대기
      if (!sessionData || !sessionData.store_id) {
        console.log('[OrderPage] WebSocket 초기화 대기 - 세션 데이터 없음');
        return;
      }

      const storeId = sessionData.store_id; // 세션에서 storeId 가져오기
      console.log('[OrderPage] WebSocket에 사용할 storeId:', storeId);
      try {
        // practiceMenuDB 전용 리스너만 제거 (다른 컴포넌트 리스너는 유지)
        // cartUpdated는 특정 콜백만 제거하도록 cleanup에서 처리
        wsService.onUserCountUpdate(null);
        wsService.onConnectionStateChange(null);
        wsService.onMemberAddedToCart(null);
        wsService.onOrderCompleted(null);
        // wsService.onCartUpdated(null); // 제거하지 않음! 다른 컴포넌트의 리스너 유지
        wsService.onCartRequestReceived(null);
        wsService.onCartResponse(null);

        // 사용자 수 업데이트 콜백
        wsService.onUserCountUpdate((count) => {
          console.log('현재 접속자 수:', count);

          // 이전 값과 비교
          const prevCount = prevActiveUsersRef.current;

          // 사용자가 2명 이상이고 이전보다 증가했을 때만 토스트 표시
          if (count >= 2 && count > prevCount) {
            const checkIcon = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>';
            const message = `${checkIcon}${count}명이 같이 주문하고 있어요!`;
            showToast(message);
          }

          // 상태 업데이트
          setActiveUsers(count);
          prevActiveUsersRef.current = count;
        });

        // 연결 상태 콜백
        wsService.onConnectionStateChange((connected, status) => {
          setWsConnected(connected);
          if (status === 'failed') {
            console.error('WebSocket 연결 실패');
            setActiveUsers(1);
          }
        });

        // 다른 멤버가 장바구니에 추가했을 때
        wsService.onMemberAddedToCart(() => {
          console.log('다른 멤버가 장바구니에 메뉴를 추가했습니다');

          // 체크 아이콘이 있는 토스트
          const checkIcon = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>';
          const message = `${checkIcon}멤버가 메뉴를 담았어요!`;

          showToast(message);
        });

        // 다른 멤버가 주문을 완료했을 때
        wsService.onOrderCompleted(() => {
          console.log('다른 멤버가 주문을 완료했습니다');

          // 장바구니 비우기
          localStorage.removeItem('cartData');
          setCartData([]);
          setCartCount(0);
          setCartTotal(0);
          loadCartData();

          // 장바구니 창 닫기
          setIsCartOpen(false);
          // 주문내역 창 닫기
          setIsOrderHistoryOpen(false);

          // 체크 아이콘이 있는 토스트
          const checkIcon = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>';
          const message = `${checkIcon}멤버가 메뉴를 주문했어요!`;
          showToast(message);
        });


        // 다른 사용자가 장바구니를 요청했을 때
        wsService.onCartRequestReceived((requesterId) => {
          // console.log('장바구니 요청 받음 from:', requesterId);

          // 현재 장바구니 데이터 전송
          const currentCartData = JSON.parse(localStorage.getItem('cartData') || '[]');
          wsService.sendCartResponse(requesterId, currentCartData);
        });

        // 장바구니 요청에 대한 응답 받았을 때
        wsService.onCartResponse((receivedCartData) => {
          // console.log('장바구니 응답 받음:', receivedCartData);

          // 받은 장바구니로 초기화
          localStorage.setItem('cartData', JSON.stringify(receivedCartData));
          setCartData(receivedCartData);
          loadCartData();
        });

        // 서버에서 장바구니 상태 수신 (하이브리드 - Single Source of Truth)
        cartUpdateHandler = (receivedCartData) => {
          console.log('서버 장바구니 상태 수신:', receivedCartData);

          // 서버 데이터를 그대로 사용
          const updatedCartData = receivedCartData || [];

          // localStorage 업데이트
          localStorage.setItem('cartData', JSON.stringify(updatedCartData));

          // 상태 직접 업데이트
          setCartData(updatedCartData);

          // 총 개수 계산 및 업데이트
          const totalCount = updatedCartData.reduce((sum, item) => sum + item.quantity, 0);
          setCartCount(totalCount);

          // 총 가격 계산 및 업데이트
          const totalPrice = updatedCartData.reduce((sum, item) => {
            console.log('아이템 가격 정보:', {
              item: item,
              price: item.price,
              quantity: item.quantity,
              menuItem: item.menuItem
            });

            // price가 menuItem 객체 안에 있을 수도 있음
            let itemPrice = 0;
            if (item.price !== undefined) {
              itemPrice = Number(item.price) || 0;
            } else if (item.menuItem && item.menuItem.price !== undefined) {
              itemPrice = Number(item.menuItem.price) || 0;
            }

            const quantity = Number(item.quantity) || 0;
            const subtotal = itemPrice * quantity;

            console.log(`가격 계산: ${itemPrice} * ${quantity} = ${subtotal}`);
            return sum + subtotal;
          }, 0);

          console.log('총 가격:', totalPrice);
          setCartTotal(totalPrice);
        };

        // 콜백 등록
        console.log('[OrderPage] onCartUpdated 호출 전');
        wsService.onCartUpdated(cartUpdateHandler);
        console.log('[OrderPage] onCartUpdated 호출 완료');

        // 공지사항 업데이트 콜백 등록 (API 재호출)
        wsService.onNoticeUpdated(async () => {
          console.log('[WebSocket] 공지사항 업데이트 수신 - API 재조회');
          try {
            const response = await fetch(`/api/notices/${storeId}`);
            const data = await response.json();
            if (data.success && data.notice) {
              setNoticeSettings({
                enabled: data.notice.enabled,
                texts: data.notice.texts
              });
            } else {
              setNoticeSettings({ enabled: false, texts: {} });
            }
          } catch (error) {
            console.error('[WebSocket] 공지사항 재조회 실패:', error);
          }
        });

        // 메뉴 업데이트 콜백 등록 (페이지 새로고침)
        wsService.onMenuUpdated(() => {
          console.log('[WebSocket] 메뉴 업데이트 수신 - 페이지 새로고침');
          window.location.reload();
        });

        // 배너 업데이트 콜백 등록 (API 재호출)
        wsService.onBannerUpdated(async () => {
          console.log('[WebSocket] 배너 업데이트 수신 - API 재조회');
          try {
            const response = await fetch(`/api/banners/${storeId}`);
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.images && data.images.length > 0) {
                const imageUrls = data.images.map(img => img.image_url);
                setSliderImages(imageUrls);
              } else {
                setSliderImages([]);
              }
            }
          } catch (error) {
            console.error('[WebSocket] 배너 재조회 실패:', error);
          }
        });

        // 매장 정보 업데이트 콜백 등록 (API 재호출)
        wsService.onStoreUpdated(async () => {
          console.log('[WebSocket] 매장 정보 업데이트 수신 - API 재조회');
          try {
            const response = await api.getStoreInfo(storeId);
            const storeData = response.storeInfo || response.store;
            if (response.success && storeData) {
              setStoreInfo({
                storeName: storeData.store_name || '',
                businessName: storeData.business_name || '',
                address: storeData.business_address || '',
                phoneNumber: storeData.business_phone || '',
                businessNumber: storeData.business_number || '',
                origins: storeData.ingredient_info || ''
              });
            }
          } catch (error) {
            console.error('[WebSocket] 매장 정보 재조회 실패:', error);
          }
        });

        // WebSocket 연결 (이미 연결되어 있지 않은 경우만)
        const tableCode = sessionData.table_code || '1';
        if (!wsService.isSocketConnected()) {
          wsService.connect(storeId, tableCode);
          console.log('WebSocket 연결 시작, 매장 ID:', storeId, '테이블:', tableCode);
        } else {
          console.log('WebSocket 이미 연결됨, 리스너만 재등록');
        }

        // 리뷰 설정 업데이트 콜백 등록 (connect 후에 등록해야 socket이 존재함)
        if (wsService.socket) {
          wsService.socket.on('review-settings-updated', (data) => {
            console.log('[WebSocket] 리뷰 설정 업데이트 수신:', data);
            if (data.isEnabled !== undefined) {
              loadReviewSettings();
            }
          });
        }

        // 연결 후 잠시 대기 후 장바구니 요청
        setTimeout(() => {
          if (wsService.isSocketConnected()) {
            // console.log('기존 사용자에게 장바구니 요청');
            wsService.requestCart();
          }
        }, 500);
      } catch (error) {
        console.error('WebSocket 초기화 실패:', error);
        setActiveUsers(1);
        setWsConnected(false);
      }
    };

    initWebSocket();

    // cleanup 함수
    return () => {
      // console.log('WebSocket cleanup 실행');
      // 특정 cart update 콜백만 제거
      if (cartUpdateHandler) {
        wsService.offCartUpdated(cartUpdateHandler);
      }

      // 다른 콜백들은 null로 제거 (이들은 단일 콜백)
      wsService.onUserCountUpdate(null);
      wsService.onConnectionStateChange(null);
      wsService.onMemberAddedToCart(null);
      wsService.onOrderCompleted(null);
      wsService.onCartRequestReceived(null);
      wsService.onCartResponse(null);
      wsService.onNoticeUpdated(null);
      wsService.onMenuUpdated(null);
      wsService.onBannerUpdated(null);
      wsService.onStoreUpdated(null);

      // 리뷰 설정 업데이트 리스너 제거
      if (wsService.socket) {
        wsService.socket.off('review-settings-updated');
      }

      // 연결 종료
      wsService.disconnect();
    };
  }, [sessionData]); // 세션 데이터 변경 시 WebSocket 재연결

  // 메뉴 데이터가 없을 때 기본값 설정
  const defaultMenuData = {
    '치킨': [],
    '사이드': [],
    '음료': [],
    '세트메뉴': [],
    '메인메뉴': [],
    '전골류': [],
    '계란': [],
    '추가메뉴': [],
    '콤보 시리즈': []
  };

  // defaultHoneyComboData - 미사용으로 주석 처리

  // 카테고리 리스트 - DB에서 받은 순서만 사용
  // console.log('Current menuData:', menuData);
  // console.log('Category order from state:', categoryOrder);

  const allCategories = categoryOrder.length > 0
    ? categoryOrder  // DB에서 받은 순서만 사용
    : [];  // DB 데이터가 없으면 빈 배열
  // console.log('All categories:', allCategories);

  // 메뉴가 있는 카테고리만 필터링 (네이버 리뷰 카테고리 제외)
  const categories = allCategories.filter(category => {
    // 카테고리명에 "네이버" 또는 "리뷰"가 포함되면 손님 페이지에서 숨김 (리뷰 보상 전용)
    if (category.includes('네이버') || category.includes('리뷰')) {
      return false;
    }
    const currentMenuData = menuData || defaultMenuData;
    return currentMenuData[category] && currentMenuData[category].length > 0;
  });
  // console.log('Filtered categories:', categories);

  // 메뉴 확장


  // 리플 효과 생성
  const createRipple = (element, event) => {
    // 스크롤 중이면 리플 생성 안함
    if (isScrolling) {
      return;
    }
    
    const ripple = document.createElement('span');
    ripple.classList.add('ripple');
    
    // 요소의 크기 가져오기
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    
    // 정중앙에 배치
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = '50%';
    ripple.style.top = '50%';
    ripple.style.marginLeft = `-${size / 2}px`;
    ripple.style.marginTop = `-${size / 2}px`;
    
    // 리플 추가
    element.appendChild(ripple);
    
    // 일반 클릭 시 애니메이션
    ripple.classList.add('click');
    
    // 애니메이션 종료 후 제거
    ripple.addEventListener('animationend', () => {
      ripple.remove();
    }, { once: true });
  };


  // 하이라이트 위치 업데이트
  const updateHighlight = (tabIndex) => {
    if (tabRefs.current[tabIndex]) {
      const tab = tabRefs.current[tabIndex];
      const isCurrentlyActive = tab.classList.contains('active');
      
      if (isCurrentlyActive) {
        setHighlightStyle({
          left: tab.offsetLeft,
          width: tab.offsetWidth,
        });
        return;
      }
      
      const tabText = tab.innerText;
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = `
        position: absolute;
        visibility: hidden;
        font-size: 15px;
        font-weight: 700;
        white-space: nowrap;
        font-family: "Pretendard", -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif;
        line-height: 18px;
        letter-spacing: 0;
      `;
      tempDiv.textContent = tabText;
      document.body.appendChild(tempDiv);
      
      const predictedWidth = tempDiv.offsetWidth;
      document.body.removeChild(tempDiv);
      
      const currentCenter = tab.offsetLeft + tab.offsetWidth / 2;
      const newLeft = currentCenter - predictedWidth / 2;
      
      setHighlightStyle({
        left: newLeft,
        width: predictedWidth,
      });
    }
  };

  // 탭을 화면 중앙으로 스크롤하는 함수
  const scrollTabToCenter = (tabIndex) => {
    const container = tabContainerRef.current;
    const tab = tabRefs.current[tabIndex];
    
    if (container && tab) {
      const containerWidth = container.offsetWidth;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      
      const tabCenter = tabLeft + (tabWidth / 2);
      const containerCenter = containerWidth / 2;
      const scrollLeft = tabCenter - containerCenter;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // 탭 클릭 핸들러
  const handleTabClick = (index) => {
    // 이전 스크롤 타이머들 취소
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }
    
    // 즉시 스크롤 리스너 비활성화
    setIsScrollListenerActive(false);
    setActiveTab(index);
    scrollTabToCenter(index);
    
    if (categoryHeaderRefs.current[index]) {
      const tabsHeight = tabsRef.current?.offsetHeight || 51;
      const categoryBadgeContainer = categoryHeaderRefs.current[index];

      const rect = categoryBadgeContainer.getBoundingClientRect();
      const scrollTop = document.body.scrollTop || window.pageYOffset;
      const elementTop = rect.top + scrollTop;

      const targetPosition = elementTop - tabsHeight - 40;

      const scrollDistance = Math.abs(targetPosition - scrollTop);

      // 스크롤 시간 계산 (더 정확하게)
      const scrollDuration = Math.min(Math.max(scrollDistance * 0.5, 400), 800);

      document.body.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      // 스크롤 애니메이션이 완전히 끝난 후에만 리스너 활성화
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrollListenerActive(true);
      }, scrollDuration + 500);
    } else {
      // 스크롤할 필요가 없는 경우에도 약간의 딜레이 후 활성화
      setTimeout(() => {
        setIsScrollListenerActive(true);
      }, 300);
    }
  };

  // 이미지 로딩 에러 처리
  const handleImageError = (index) => {
    setImageLoadErrors(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const handleImageLoad = (index) => {
    setImageLoadErrors(prev => ({
      ...prev,
      [index]: false
    }));
  };

  // 항상 최신 currentSlide를 setInterval 콜백에서 참조 (closure 캡처 회피)
  const currentSlideRef = useRef(currentSlide);
  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  // currentSlide 변경 시: 활성 비디오는 바로 재생(첫 프레임 이미 렌더됨), 비활성은 멈추고 0초로 되감기
  // 비활성을 0초로 되감아야 다음에 활성화될 때 첫 프레임이 보이는 상태에서 시작 → 검정 깜빡임 제거
  useEffect(() => {
    const active = currentSlide;
    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (!video) return;
      const idx = parseInt(key, 10);
      if (idx === active) {
        video.play().catch(() => {});
      } else {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
      }
    });
  }, [currentSlide]);

  const startAutoPlay = () => {
    // 이미지가 1개일 때는 자동 재생하지 않음
    if (sliderImages.length <= 1) {
      return;
    }
    stopAutoPlay(); // 기존 interval을 먼저 정리
    autoPlayRef.current = setInterval(() => {
      // 현재 슬라이드가 비디오면 자동 advance하지 않음 — 비디오 onEnded가 처리
      const cur = currentSlideRef.current;
      if (isVideoUrl(extendedImages[cur])) return;
      nextSlide();
    }, 2500);
  };

  const stopAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const goToSlide = (index) => {
    const newSlide = index + 1;
    setCurrentSlide(newSlide);
    setRealIndex(index);
  };

  const nextSlide = () => {
    setCurrentSlide(prev => {
      const next = prev + 1;
      // dot 인덱스를 즉시 업데이트
      if (next === extendedImages.length - 1) {
        setRealIndex(0);
      } else if (next >= 1 && next <= sliderImages.length) {
        setRealIndex(next - 1);
      }
      return next;
    });
  };

  const prevSlide = () => {
    setCurrentSlide(prev => {
      const prevSlide = prev - 1;
      // dot 인덱스를 즉시 업데이트
      if (prevSlide === 0) {
        setRealIndex(sliderImages.length - 1);
      } else if (prevSlide >= 1 && prevSlide <= sliderImages.length) {
        setRealIndex(prevSlide - 1);
      }
      return prevSlide;
    });
  };

  const handleDragStart = (clientX) => {
    setIsDragging(true);
    setDragStart(clientX);
    setDragOffset(0);
    stopAutoPlay();
  };

  const handleDragMove = (clientX) => {
    if (!isDragging) return;
    const offset = clientX - dragStart;
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const threshold = 50;
    
    if (dragOffset > threshold) {
      prevSlide();
    } else if (dragOffset < -threshold) {
      nextSlide();
    }
    
    setDragOffset(0);

    // 이미지가 2개 이상일 때만 자동 재생 시작
    if (sliderImages.length > 1) {
      startAutoPlay();
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // 장바구니 데이터 로드 및 계산
  const loadCartData = () => {
    const storedCart = JSON.parse(localStorage.getItem('cartData') || '[]');
    setCartData(storedCart);

    // 총 수량 계산
    const totalCount = storedCart.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(totalCount);

    // 총 가격 계산
    const totalPrice = storedCart.reduce((sum, item) => {
      // price 필드가 있으면 우선 사용 (옵션 포함 가격이 미리 계산된 경우)
      if (item.price !== undefined && item.price !== null) {
        return sum + (item.price * item.quantity);
      }

      // 선택 메뉴에서 생성된 항목인 경우 (이미 독립적인 메뉴로 변환됨)
      if (item.isFromChoiceMenu) {
        const price = item.menuItem.price || item.menuItem.basePrice || 0;
        return sum + (price * item.quantity);
      }

      // 선택 메뉴 타입 체크 (구버전 호환)
      const isChoiceType = item.menuItem?.menuType === '선택';

      // 선택 메뉴인 경우 선택한 옵션의 가격만 사용 (구버전 호환)
      if (isChoiceType && item.selectedOptions) {
        // 선택된 옵션 찾기
        const selectedOption = Array.isArray(item.selectedOptions)
          ? item.selectedOptions[0]
          : Object.values(item.selectedOptions)[0];

        if (selectedOption && selectedOption.price) {
          const optionPrice = parseInt(selectedOption.price.toString().replace(/[^0-9]/g, '')) || 0;
          return sum + (optionPrice * item.quantity);
        }
        return sum;
      }

      // 고정세트 메뉴 체크
      const isFixedSet = item.menuItem?.menuType === '고정세트';

      // 다국어 구조 지원
      const price = item.menuItem?.ko?.price || item.menuItem?.price || '0';
      const basePrice = parseInt((price || '0').toString().replace(/[^0-9]/g, '')) || 0;

      // 고정세트인 경우 옵션 가격을 더하지 않음
      if (isFixedSet) {
        return sum + (basePrice * item.quantity);
      }

      let optionsPrice = 0;

      if (item.selectedOptions) {
        // 새 형식 (배열) 체크
        if (Array.isArray(item.menuItem.options)) {
          // 새 메뉴 형식: selectedOptions가 {optionName_index: choice} 형태
          Object.entries(item.selectedOptions).forEach(([key, choice]) => {
            if (choice && choice.price) {
              const price = parseInt(choice.price.toString().replace(/[^0-9]/g, '')) || 0;
              optionsPrice += price;
            }
          });
        }
        // 기존 형식 (객체)
        else if (item.menuItem.options) {
          Object.entries(item.menuItem.options).forEach(([optionTitle, optionData], index) => {
            const selected = item.selectedOptions[index];

            if (optionData.type === 'required' && selected) {
              const option = optionData.items.find(opt => opt.name === selected);
              if (option && option.price) {
                optionsPrice += option.price;
              }
            } else if (optionData.type === 'optional' && Array.isArray(selected)) {
              selected.forEach(optionName => {
                const option = optionData.items.find(opt => opt.name === optionName);
                if (option && option.price) {
                  optionsPrice += option.price;
                }
              });
            }
          });
        }
      }

      return sum + ((basePrice + optionsPrice) * item.quantity);
    }, 0);

    setCartTotal(totalPrice);
  };

  // 컴포넌트 마운트시 장바구니 데이터 로드
  useEffect(() => {
    loadCartData();
    
    // localStorage 변경 감지 (다른 탭에서 변경시)
    const handleStorageChange = () => {
      loadCartData();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // BroadcastChannel로 실시간 동기화
    let channel;
    try {
      channel = new BroadcastChannel('cart_channel');
      channel.onmessage = (event) => {
        if (event.data.type === 'cart_updated') {
          loadCartData();
        } else if (event.data.type === 'cart-cleared') {
          // 장바구니가 비워졌을 때
          setCartData([]);
          setCartCount(0);
          setCartTotal(0);
        }
      };
    } catch (error) {
      console.log('BroadcastChannel not supported:', error);
    }
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (channel) {
        channel.close();
      }
    };
  }, []);

  // 옵션 화면이 닫힐 때마다 장바구니 데이터 새로고침
  useEffect(() => {
    if (!isBottomSheetOpen) {
      loadCartData();
    }
  }, [isBottomSheetOpen]);

  // Listen for localStorage changes from admin page
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'noticeSettings') {
        const newSettings = e.newValue ? JSON.parse(e.newValue) : { enabled: true, texts: {} };
        setNoticeSettings(newSettings);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 공지사항 텍스트가 1줄 이상인지 확인 (언어/공지 변경 시)
  useEffect(() => {
    if (noticeContentRef.current && noticeSettings.enabled) {
      const checkLines = () => {
        const textEl = noticeContentRef.current?.querySelector('.notice-text');
        if (!textEl) return;
        // scrollHeight > clientHeight 이면 텍스트가 잘린 것
        const needsExpand = textEl.scrollHeight > textEl.clientHeight;
        setNoticeNeedsExpand(needsExpand);
      };
      // DOM 업데이트 후 체크
      setTimeout(checkLines, 50);
    } else {
      setNoticeNeedsExpand(false);
    }
  }, [selectedLanguage, noticeSettings]);
  
  // 가격 변경 시 슬롯머신 효과
  useEffect(() => {
    const newPrice = cartTotal.toLocaleString();
    
    // 장바구니가 비어있을 때
    if (cartCount === 0) {
      setDisplayPrice('0');
      setPrevPrice('0');
      setIsInitialized(false);
      return;
    }
    
    // 초기화되지 않은 상태에서 처음 장바구니에 아이템이 추가될 때
    if (!isInitialized && cartCount > 0) {
      // 애니메이션 없이 바로 현재 가격으로 설정
      setDisplayPrice(newPrice);
      setPrevPrice(newPrice);
      setIsInitialized(true);
      return;
    }
    
    // 일반적인 가격 변경 (초기화 이후)
    if (isInitialized && newPrice !== displayPrice && !priceAnimating) {
      setPrevPrice(displayPrice);
      setAnimationKey(prev => prev + 1);
      
      // 가격 증가/감소 판단
      const prevPriceNum = parseInt(String(displayPrice).replace(/,/g, '')) || 0;
      const newPriceNum = parseInt(String(newPrice).replace(/,/g, '')) || 0;
      setIsPriceIncreasing(newPriceNum > prevPriceNum);
      
      setPriceAnimating(true);
      
      // 실제 가격 업데이트는 50ms 후
      setTimeout(() => {
        setDisplayPrice(newPrice);
      }, 50);
      
      // 애니메이션 종료
      setTimeout(() => {
        setPriceAnimating(false);
      }, 600);
    }
  }, [cartTotal, cartCount, displayPrice, priceAnimating, isInitialized]);
  
  // cart-count 변경 시 애니메이션
  useEffect(() => {
    // 장바구니가 비어있을 때
    if (cartCount === 0) {
      setDisplayCount(0);
      setPrevCount(0);
      setIsCountInitialized(false);
      return;
    }
    
    // 초기화되지 않은 상태에서 처음 장바구니에 아이템이 추가될 때
    if (!isCountInitialized && cartCount > 0) {
      // 애니메이션 없이 바로 현재 수량으로 설정
      setDisplayCount(cartCount);
      setPrevCount(cartCount);
      setIsCountInitialized(true);
      return;
    }
    
    // 일반적인 수량 변경 (초기화 이후)
    if (isCountInitialized && cartCount !== displayCount && !countAnimating) {
      setPrevCount(displayCount);
      setCountAnimationKey(prev => prev + 1);
      setIsCountIncreasing(cartCount > displayCount);
      setCountAnimating(true);
      
      // 실제 수량 업데이트는 약간의 딜레이 후
      setTimeout(() => {
        setDisplayCount(cartCount);
      }, 50);
      
      // 애니메이션 종료
      setTimeout(() => {
        setCountAnimating(false);
      }, 300);
    }
  }, [cartCount, displayCount, countAnimating, isCountInitialized]);
  
  // 스크롤 감지 useEffect
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = () => {
      setIsScrolling(true);
      
      // 기존 타임아웃 취소
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // 스크롤이 멈춘 후 150ms 후에 스크롤 상태 해제
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  useEffect(() => {
    if (isTabletLayout) return; // 태블릿에는 sticky 탭 바가 없다
    const handleScroll = () => {
      if (tabsRef.current) {
        const tabsTop = tabsRef.current.offsetTop;
        const scrollTop = window.pageYOffset;
        setIsSticky(scrollTop > tabsTop);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isTabletLayout]);

  useEffect(() => {
    if (isTabletLayout) return; // 태블릿은 사이드바로 카테고리를 고른다(스크롤 스파이 불필요)
    const handleUserScroll = () => {
      if (!isScrollListenerActive) {
        return;
      }
      
      const tabsHeight = tabsRef.current?.offsetHeight || 51;
      const triggerPoint = tabsHeight + 120;
      
      let newActiveTab = 0;
      
      for (let i = categoryHeaderRefs.current.length - 1; i >= 0; i--) {
        const categoryBadgeContainer = categoryHeaderRefs.current[i];
        if (categoryBadgeContainer) {
          const rect = categoryBadgeContainer.getBoundingClientRect();
          const badgeTop = rect.top;
          
          if (badgeTop <= triggerPoint) {
            newActiveTab = i;
            break;
          }
        }
      }
      
      if (newActiveTab !== activeTab) {
        setActiveTab(newActiveTab);
        scrollTabToCenter(newActiveTab);
      }
      
    };
    
    window.addEventListener('scroll', handleUserScroll, { passive: true });
    
    setTimeout(handleUserScroll, 100);
    
    return () => {
      window.removeEventListener('scroll', handleUserScroll);
    };
  }, [activeTab, isScrollListenerActive, categories, isTabletLayout]);

  useEffect(() => {
    updateHighlight(activeTab);
  }, [activeTab]);

  // 언어 변경 및 카테고리 번역 데이터 로드 시 하이라이트 재조정
  useEffect(() => {
    // DOM이 업데이트된 후 하이라이트 재계산
    const timer = setTimeout(() => {
      updateHighlight(activeTab);
    }, 50);

    return () => clearTimeout(timer);
  }, [selectedLanguage, activeTab]);

  // 카테고리가 처음 로드될 때 하이라이트 초기화
  useEffect(() => {
    if (categories && categories.length > 0) {
      // 카테고리 로드 후 DOM이 렌더링될 시간을 준 다음 하이라이트 설정
      const timer = setTimeout(() => {
        updateHighlight(activeTab);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [categories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollTabToCenter(0);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isTabletLayout) return; // 태블릿에는 배너 슬라이더가 없다 (2.5초마다 리렌더 방지)
    if (sliderImages.length > 1) {
      startAutoPlay();
    }
    return () => stopAutoPlay();
  }, [sliderImages.length, isTabletLayout]);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = () => {
        handleMouseUp();
      };
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isDragging) {
      if (currentSlide === 0) {
        const timer = setTimeout(() => {
          setIsTransitioning(true);
          setCurrentSlide(sliderImages.length);
          const resetTimer = setTimeout(() => {
            setIsTransitioning(false);
          }, 100);
          return () => clearTimeout(resetTimer);
        }, 500);
        return () => clearTimeout(timer);
      } else if (currentSlide === extendedImages.length - 1) {
        const timer = setTimeout(() => {
          setIsTransitioning(true);
          setCurrentSlide(1);
          const resetTimer = setTimeout(() => {
            setIsTransitioning(false);
          }, 100);
          return () => clearTimeout(resetTimer);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [currentSlide, isDragging, sliderImages.length, extendedImages.length]);

  useEffect(() => {
    if (isTabletLayout) return; // 태블릿은 TabletOrderView 가 자체 스케일러를 쓴다
    const scaleUI = () => {
      const wrapper = document.querySelector('.scale-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    scaleUI();
    window.addEventListener('resize', scaleUI);
    return () => window.removeEventListener('resize', scaleUI);
  }, [isTabletLayout]);


  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, []);

  // 메뉴 클릭 핸들러
  const handleMenuClick = (item) => {
    setSelectedMenuItem(item);
    setIsBottomSheetOpen(true);
  };

  const handleQuickAdd = (item, e) => {
    e.stopPropagation();
    if (item.soldOut || item.is_soldout || calculateTimeRemaining(item)) return;

    const hasOptionalOption = (() => {
      if (item.options && Array.isArray(item.options)) {
        return item.options.some(group => !group.required);
      } else if (item.options && typeof item.options === 'object') {
        return Object.values(item.options).some(opt => opt.type !== 'required');
      }
      return false;
    })();

    if (hasOptionalOption) {
      handleMenuClick(item);
      return;
    }

    const selectedOptionsArray = [];
    const groupTypeMap = {};

    if (item.options && Array.isArray(item.options)) {
      item.options.forEach((group) => {
        const groupName = group.option_group_name || group.name;
        groupTypeMap[groupName] = group.option_type;
      });

      item.options.forEach((optionGroup) => {
        const isRequired = optionGroup.required;
        if (isRequired) {
          const optionItems = optionGroup.choices || optionGroup.items || [];
          if (optionItems.length > 0) {
            const firstChoice = optionItems[0];
            const groupName = optionGroup.name || optionGroup.option_group_name;
            const optionType = groupTypeMap[groupName] || '';
            selectedOptionsArray.push({
              groupId: `${groupName}_0`,
              name: firstChoice.name || firstChoice.option_name,
              price: firstChoice.price ?? firstChoice.option_price ?? 0,
              option_type: optionType,
              sda_cls_cd: firstChoice.sda_cls_cd || '',
              sda_cd: firstChoice.sda_cd || firstChoice.option_id || '',
              sds_grp_cd: firstChoice.sds_grp_cd || '',
              sds_cls_cd: firstChoice.sds_cls_cd || '',
              sds_cd: firstChoice.sds_cd || firstChoice.option_id || '',
              sds_prod_cd: firstChoice.sds_prod_cd || '',
              ...firstChoice
            });
          }
        }
      });
    } else if (item.options && typeof item.options === 'object') {
      Object.entries(item.options).forEach(([optionTitle, optionData], index) => {
        if (optionData.type === 'required' && optionData.items.length > 0) {
          const firstItem = optionData.items[0];
          selectedOptionsArray.push({
            groupId: `${optionTitle}_0`,
            name: firstItem.name,
            price: firstItem.price || 0,
            option_type: '',
            ...firstItem
          });
        }
      });
    }

    const price = item.basePrice || item.price;
    const priceStr = typeof price === 'number' ? price.toString() : (price || '0');
    const basePrice = parseInt(priceStr.toString().replace(/[^0-9]/g, '')) || 0;
    let optionsPrice = 0;
    selectedOptionsArray.forEach(opt => {
      const p = opt.price ?? 0;
      optionsPrice += parseInt(p.toString().replace(/[^0-9]/g, '')) || 0;
    });
    const totalPrice = basePrice + optionsPrice;

    const menuItemWithLang = {
      ...item,
      name_ko: item.name_ko || item.menu_name || item.name,
      name_en: item.name_en || item.menu_name || item.name,
      name_ja: item.name_ja || item.menu_name || item.name,
      name_zh: item.name_zh || item.menu_name || item.name
    };

    const cartItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      menuItem: menuItemWithLang,
      selectedOptions: selectedOptionsArray,
      quantity: 1,
      price: totalPrice,
      addedAt: new Date().toISOString(),
      timestamp: Date.now()
    };

    const existingCart = JSON.parse(localStorage.getItem('cartData') || '[]');
    const existingItemIndex = existingCart.findIndex(ci => {
      const isSameMenu = item.itemCode ?
        (ci.menuItem.itemCode === item.itemCode) :
        (ci.menuItem.id === item.id);
      if (!isSameMenu) return false;
      const existingOptions = Array.isArray(ci.selectedOptions) ? ci.selectedOptions : [];
      if (existingOptions.length !== selectedOptionsArray.length) return false;
      const sortOpts = (opts) => opts.map(o => ({
        groupId: o.groupId || '', itemCode: o.itemCode || '', name: o.name || ''
      })).sort((a, b) => a.groupId.localeCompare(b.groupId) || a.name.localeCompare(b.name));
      return JSON.stringify(sortOpts(existingOptions)) === JSON.stringify(sortOpts(selectedOptionsArray));
    });

    let updatedCart;
    if (existingItemIndex !== -1) {
      updatedCart = [...existingCart];
    } else {
      updatedCart = [...existingCart, cartItem];
    }

    localStorage.setItem('cartData', JSON.stringify(updatedCart));

    if (wsService.isSocketConnected()) {
      const itemToSend = existingItemIndex !== -1 ? {
        menuItem: item,
        selectedOptions: selectedOptionsArray,
        quantity: 1,
        price: totalPrice,
        addedAt: new Date().toISOString(),
        timestamp: Date.now()
      } : cartItem;
      wsService.addCartItem(itemToSend);
    }

    try {
      const channel = new BroadcastChannel('cart_channel');
      channel.postMessage({ type: 'cart_updated' });
      channel.close();
    } catch (error) {}

    const checkIcon = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>';
    showToast(
      selectedLanguage === 'ko' ? checkIcon + '메뉴를 담았어요!' :
      selectedLanguage === 'en' ? checkIcon + 'Menu added!' :
      selectedLanguage === 'ja' ? checkIcon + '追加しました！' :
      checkIcon + '已加入购物车！'
    );
    loadCartData();
  };

  const handleBottomSheetClose = () => {
    setIsBottomSheetOpen(false);
    setTimeout(() => {
      setSelectedMenuItem(null);
    }, 300);
  };

  return (
    <>
      <style jsx="true">{`

          @font-face {
            font-family: 'Pretendard';
            src: url('https://woworderbucket.s3.ap-northeast-2.amazonaws.com/fonts/PretendardVariable.woff2') format('woff2-variations'),
            font-display: swap;
            font-weight: 100 900; /* Variable font 범위 */
          }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif;
          -webkit-tap-highlight-color: transparent;
          -webkit-text-size-adjust:none;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        
        body {
          background-color: #ffffff;
        }
        
        .scale-wrapper {
          width: 360px;
          transform-origin: top center;
          margin: 0 auto;
          position: relative;
        }
        
        @media screen and (min-width: 361px) {
          .scale-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .scale-wrapper {
            zoom: 1.5;
          }
        }

        .top-container {
          width: 360px;
          height: 229px;
          position: relative;
          overflow: hidden;
        }
        
        .image-slider {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          cursor: grab;
          user-select: none;
        }
        
        .image-slider.dragging {
          cursor: grabbing;
        }
        
        .slider-wrapper {
          display: flex;
          width: 700%;
          height: 100%;
          transition: transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          will-change: transform;
        }
        
        .slider-wrapper.no-transition {
          transition: none;
        }
        
        .slider-image {
          width: 14.285714%;
          height: 100%;
          object-fit: cover;
          flex-shrink: 0;
          pointer-events: none;
          background-color: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .image-placeholder {
          width: 14.285714%;
          height: 100%;
          background: linear-gradient(135deg, #FF6B35, #F7931E);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          font-weight: 600;
          text-align: center;
          flex-shrink: 0;
          text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
        }
        
        .image-loading {
          width: 14.285714%;
          height: 100%;
          background-color: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #666;
          font-size: 12px;
        }

        .banner-pagination-dots {
          display: none;
          gap: 4px;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          bottom: 8px;
          z-index: 2;
        }

        .banner-dot {
          width: 6px;
          height: 6px;
          min-width: 6px;
          min-height: 6px;
          max-width: 6px;
          max-height: 6px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: background-color 0.3s ease;
          border: none;
          outline: none;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }

        .banner-dot.active {
          background-color: rgba(255, 255, 255, 1);
        }

        .language-selector {
          position: absolute;
          top: 10px;
          right: 10px;
          height: 35px;
          border-radius: 12px;
          background-color: rgba(255, 255, 255, 0.8);
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          cursor: pointer;
          display: flex;
          align-items: center;
          z-index: 3;
          transition: width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .language-selector.collapsed {
          width: 35px;
        }
        
        .language-selector.expanded {
          width: 260px;
        }
        
        .language-options {
          display: flex;
          align-items: center;
          height: 100%;
          white-space: nowrap;
          padding: 0 28px 0 10px;
          opacity: 0;
          transition: opacity 0.2s ease;
          flex: 1;
          gap: 12px;
        }
        
        .language-selector.expanded .language-options {
          opacity: 1;
          transition: opacity 0.2s ease 0.15s;
          pointer-events: auto;
        }
        
        .language-selector.collapsed .language-options {
          opacity: 0;
          transition: opacity 0.15s ease;
          pointer-events: none;
        }
        
        .language-option {
          font-size: 14px;
          font-weight: 400;
          color: #666666;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
          user-select: none;
          opacity: 0;
          transform: translateX(20px);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .language-flag {
          width: 20px;
          height: 20px;
          object-fit: cover;
        }
        
        .language-selector.expanded .language-option {
          opacity: 1;
          transform: translateX(0);
        }
        
        .language-selector.expanded .language-option:nth-child(1) {
          transition: opacity 0.3s ease 0.25s, transform 0.3s ease 0.25s, color 0.2s ease;
        }
        
        .language-selector.expanded .language-option:nth-child(2) {
          transition: opacity 0.3s ease 0.2s, transform 0.3s ease 0.2s, color 0.2s ease;
        }
        
        .language-selector.expanded .language-option:nth-child(3) {
          transition: opacity 0.3s ease 0.15s, transform 0.3s ease 0.15s, color 0.2s ease;
        }
        
        .language-selector.expanded .language-option:nth-child(4) {
          transition: opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s, color 0.2s ease;
        }
        
        .language-selector.collapsed .language-option {
          transition: opacity 0.15s ease, transform 0.15s ease;
        }
        
        .language-option:hover {
          color: #333333;
        }
        
        .language-option.active {
          color: #000000;
          font-weight: 600;
        }
        
        .language-icon {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          width: 19px;
          height: 19px;
          cursor: default;
          z-index: 10;
          pointer-events: none;
        }

        .main-container {
          width: 360px;
          position: relative;
        }

        .store-section {
          width: 360px;
          padding: 16px;
          padding-bottom: ${noticeSettings.enabled ? '16px' : '20px'};
          box-sizing: border-box;
          border-bottom: 8px solid #F3F5F7;
        }

        .store-info-container {
          width: 100%;
          box-sizing: border-box;
        }

        .store-info {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          width: 328px;
          min-height: 31px;
          margin-bottom: 20px;
        }

        .store-name {
          flex: 1;
          min-width: 0;
          font-size: 26px;
          font-weight: 700;
          color: #000000;
          line-height: 31px;
          letter-spacing: 0;
          word-break: break-all;
        }
        /* 매장명 길이별 폰트 축소 (10자+ / 15자+) */
        .store-name.long { font-size: 23px; line-height: 27px; }
        .store-name.xlong { font-size: 16px; line-height: 20px; }

        .table-badge {
          padding: 0 5px;
          width: auto;
          height: 24px;
          background-color: #f1f1f1;
          color: #959595;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 400;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 14px;
        }

        .action-buttons {
          display: flex;
          gap: 13px;
          width: 328px;
          height: 62px;
          margin-bottom: 0px;
        }

        .action-btn {
          width: 62px;
          height: 62px;
          background-color: #ffffff;
          border-radius: 20px;
          box-shadow: 0 0px 8px rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 5px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
          transition: transform 0.12s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.12s ease;
          transform-origin: center;
        }
        
        .action-btn:active {
          transform: scale(0.92);
        }

        .action-btn img {
          width: 36px;
          height: 36px;
        }

        .action-btn span {
          font-size: 9px;
          font-weight: 500;
          color: #000000;
          line-height: 11px;
          text-align: center;
        }

        .live-status {
          width: 180px;
          height: 62px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .live-status-content {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        
        .live-status-content img {
          width: 30px;
          height: 30px;
          margin-bottom: 2px;
        }
        
        .live-status-content span {
          font-size: 10px;
          font-weight: 700;
          text-align: center;
          line-height: 12px;
        }

        .notice-container {
          width: 100%;
          padding-top: 19px;
          margin: 0;
          box-sizing: border-box;
        }

        .notice {
          width: 100%;
          min-height: 32px;
          max-height: 32px;
          padding: 8px 12px;
          border: 1px solid #d2d2d2;
          border-radius: 8px;
          background-color: #ffffff;
          display: flex;
          align-items: flex-start;
          position: relative;
          cursor: pointer;
          transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }
        
        .notice.expanded {
          max-height: 300px;
        }

        .notice-content {
          display: flex;
          align-items: flex-start;
          gap: 4px;
          width: calc(100% - 20px);
        }
        
        .notice-expand-btn {
          position: absolute;
          right: 12px;
          top: 13px;
          width: 10.54px;
          height: 6.07px;
          background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTEiIGhlaWdodD0iNiIgdmlld0JveD0iMCAwIDExIDYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNS41IDVMMTAgMSIgc3Ryb2tlPSIjQTBBMEEwIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=') no-repeat center;
          background-size: contain;
          cursor: pointer;
          transform-origin: center;
        }
        
        .notice.expanded .notice-expand-btn {
          transform: rotate(180deg);
        }

        .notice-icon {
          width: 14px;
          height: 14px;
          position: relative;
          flex-shrink: 0;
          margin-top: 0;
        }

        .notice-text {
          flex: 1;
          font-size: 11px;
          font-weight: 500;
          color: #000000;
          line-height: 15px;
          letter-spacing: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          line-clamp: 1;
          -webkit-box-orient: vertical;
          transition: -webkit-line-clamp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .notice.expanded .notice-text {
          -webkit-line-clamp: unset;
          line-clamp: unset;
          white-space: normal;
        }

        .category-container {
          position: sticky;
          position: -webkit-sticky;
          top: 0;
          width: 360px;
          height: 51px;
          background-color: #ffffff;
          z-index: 1000;
          transition: all 0.0375s ease;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
          will-change: transform;
          margin: 0 auto;
          left: 0;
          right: 0;
        }
        
        .category-container::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1.2px;
          background-color: #f3f5f7;
        }
        
        .category-tabs {
          display: flex;
          gap: 22px;
          padding: 16px 16px 16px 16px;
          height: 50px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          align-items: center;
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          position: relative;
        }
        
        .category-tabs.blocked {
          pointer-events: none;
        }

        .category-tabs::-webkit-scrollbar {
          display: none;
        }

        .category-tab {
          font-size: 15px;
          font-weight: 500;
          color: #797979;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.025s ease;
          height: 17px;
          line-height: 17px;
          letter-spacing: 0;
          -webkit-tap-highlight-color: transparent;
          user-select: none;
        }
        
        .category-tab.disabled {
          cursor: not-allowed;
        }

        .category-tab.active {
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          height: 18px;
          line-height: 18px;
          animation: tabBounce 0.3s ease-out;
        }
        
        @keyframes tabBounce {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .category-highlight {
          position: absolute;
          bottom: 0px;
          left: 0;
          width: 0;
          height: 2px;
          background-color: #000000;
          border-radius: 10px;
          transition: all 0.0375s ease;
          z-index: 1;
          pointer-events: none;
        }

        .menu-display-container {
          width: 360px;
          position: relative;
          padding-top: 6px;
          overflow-x: hidden;
        }

        .menu-container {
          width: 360px;
          position: relative;
          padding-top: 4px;
          padding-bottom: 4px;
        }
        
        .menu-container:not(:last-child)::after {
          content: '';
          display: block;
          width: 100%;
          height: 10px;
          background-color: #f3f5f7;
        }
        
        .menu-container:last-child {
          margin-bottom: 0;
        }

        .menu-container:first-child {
          padding-top: 0;
        }

        .category-badge-container {
          padding: 18px 16px 4px 20px;
        }
        
        .category-badge {
          background-color: #f1f1f1;
          color: #000000;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          display: inline-block;
          width: auto;
          height: auto;
          line-height: 19px;
          text-align: center;
          white-space: nowrap;
        }

        /* ===== 메뉴 검색 ===== */
        /* ※ .category-container 의 position: sticky 는 위쪽 블록에서 이미 선언됨.
              여기서 position: relative 로 덮어쓰면 sticky 가 깨지므로 자식만 스타일링. */
        .category-container .category-tabs {
          padding-right: 50px;  /* 검색 아이콘 자리 확보 — 마지막 카테고리가 가려지지 않게 */
        }
        .category-search-btn {
          position: absolute;
          top: 0;
          right: 0;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(to right, rgba(255,255,255,0) 0%, #ffffff 30%);
          border: none;
          padding: 0;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          z-index: 2;
        }
        .category-search-btn:active {
          opacity: 0.5;
        }
        .search-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 50px;
          padding: 0 12px 0 16px;
          background-color: #ffffff;
        }
        .search-input {
          flex: 1;
          height: 36px;
          border: none;
          outline: none;
          background: transparent;
          font-size: 15px;
          font-weight: 500;
          color: #000000;
          padding: 0;
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .search-input::placeholder {
          color: #c4c4c4;
          font-weight: 500;
        }
        .search-close-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .search-close-btn:active {
          opacity: 0.5;
        }
        .search-state-message {
          width: 100%;
          padding: 80px 16px;
          text-align: center;
          font-size: 14px;
          font-weight: 500;
          color: #9a9a9a;
          line-height: 22px;
        }

        .menu-item {
          display: flex;
          flex-direction: column;
          padding: 16px;
          width: 360px;
          border-bottom: 1px solid #f3f5f7;
          transition: padding 0.3s ease, background-color 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        
        
        .ripple {
          position: absolute;
          border-radius: 50%;
          background-color: rgba(0, 0, 0, 0.1);
          transform: scale(0);
          opacity: 0;
          transform-origin: center center;
          pointer-events: none;
        }
        
        .ripple.click {
          animation: rippleClick 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .ripple.persistent {
          animation: ripplePersistent 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .ripple.hold {
          animation: rippleHold 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .ripple-out {
          animation: rippleFadeOut 0.2s ease-out forwards;
        }
        
        @keyframes rippleClick {
          0% {
            transform: scale(0.4);
            opacity: 0.8;
          }
          30% {
            opacity: 0.7;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
        
        @keyframes rippleHold {
          0% {
            transform: scale(0.4);
            opacity: 0.8;
          }
          100% {
            transform: scale(2.5);
            opacity: 0.8;
          }
        }
        
        @keyframes ripplePersistent {
          0% {
            transform: scale(0.4);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.8);
            opacity: 0.6;
          }
        }
        
        @keyframes rippleFadeOut {
          from {
            opacity: 0.8;
          }
          to {
            opacity: 0;
          }
        }
        
        
        .menu-container .menu-item:last-child {
          border-bottom: none;
        }
        
        .menu-item-basic {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }

        .menu-info {
          width: 234px;
          min-height: 51px;
          padding-top: 4px;
          height: auto;
          display: flex;
          flex-direction: column;
        }

        .menu-tags {
          display: flex;
          align-items: center;
          gap: 6px;
          width: auto;
          min-height: 18px;
          margin-bottom: 6px;
        }

        .menu-tags:empty {
          margin-bottom: 0;
          min-height: 0;
        }
        
        .menu-tag {
          height: 18px;
          width: auto;
        }

        .menu-tag-most-ordered {
          font-size: 12px;
          font-weight: 700;
          line-height: 14px;
          letter-spacing: 0;
          color: #FDCD27;
          white-space: nowrap;
        }

        .menu-name {
          width: 206px;
          height: 19px;
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          margin-bottom: 6px;
          line-height: 19px;
          letter-spacing: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .menu-description {
          width: 206px;
          font-size: 12px;
          font-weight: 500;
          color: #949494;
          line-height: 18px;
          margin-bottom: 10px;
          letter-spacing: -0.24px;
          word-break: break-word;
        }

        .menu-price {
          width: 206px;
          height: 18px;
          font-size: 15px;
          font-weight: 700;
          color: #000000;
          line-height: 18px;
          letter-spacing: 0;
        }

        .menu-image-container {
          position: relative;
          width: 94px;
          height: 94px;
          overflow: hidden;
          border-radius: 6px;
          border: 1.2px solid #EEEEEE;
          box-sizing: border-box;
        }
        .menu-image-container.no-image {
          background: transparent;
          border: none;
          width: 0;
          height: 0;
          overflow: visible;
          position: static;
        }
        .menu-image-container.no-image .sold-out-overlay {
          background-color: transparent;
          width: 94px;
          height: 94px;
          top: auto;
          left: auto;
          right: 16px;
          bottom: 50%;
          transform: translateY(50%);
        }

        .quick-add-btn {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background-color: #ffffff;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
          box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.5);
          padding: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .quick-add-btn:active {
          transform: scale(0.9);
        }
        .menu-image-container.no-image .quick-add-btn {
          top: 50%;
          bottom: auto;
          right: 20px;
          transform: translateY(-50%);
        }

        .menu-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          border-radius: 5px;
        }

        .sold-out-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }

        .sold-out-badge {
          background-color: hsla(0, 0.00%, 0.00%, 0.40);
          color: #ffffff;
          padding: 3px 8px;
          min-width: 37px;
          height: auto;
          border-radius: 7px;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 14px;
          white-space: nowrap;
        }
        
        .time-limit-badge {
          background-color: hsla(0, 0.00%, 0.00%, 0.40);
          color: #ffffff;
          padding: 3px 6px;
          border-radius: 7px;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: normal;
          max-width: 86px;
          line-height: 14px;
          letter-spacing: -0.02em;
        }

        .menu-item.sold-out .menu-name,
        .menu-item.sold-out .menu-description,
        .menu-item.sold-out .menu-price {
          color: #C4C4C4;
        }
        
        .menu-item.time-limited .menu-name,
        .menu-item.time-limited .menu-description,
        .menu-item.time-limited .menu-price {
          color: #C4C4C4;
        }

        /* 옵션 관련 스타일 */
        .menu-item {
          position: relative;
        }
        

        .white-spacer {
          width: 360px;
          height: 10px;
          background-color: #ffffff;
        }

        .bottom-info {
          background-color: #f3f5f7;
          padding: 30px 16px 160px;
          width: 360px;
          min-height: 374px;
        }

        .info-section {
          width: 328px;
          margin-bottom: 20px;
        }

        .info-title {
          width: 328px;
          height: 16px;
          font-size: 13px;
          font-weight: 700;
          color: #6e6e6e;
          margin-bottom: 6px;
          line-height: 16px;
          letter-spacing: 0;
        }

        .info-content {
          width: 328px;
          font-size: 13px;
          font-weight: 400;
          color: #949494;
          line-height: 20px;
          letter-spacing: 0;
        }

        .fixed-bottom {
          background-image: url(${bottomNavImg});
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 360px;
          height: 100px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          z-index: 1000;
          pointer-events: none; /* 전체 영역 클릭 불가 */
        }

        .bottom-button {
          position: absolute;
          bottom: 0%;
          margin-bottom: 11px;
          width: 328px;
          height: 50px;
          background-color: #fdcd27;
          border-radius: 10px;
          border: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          letter-spacing: -0.32px;
          line-height: 19px;
          transition: transform 0.12s cubic-bezier(0.4, 0.0, 0.2, 1), box-shadow 0.12s ease;
          pointer-events: auto; /* 버튼만 클릭 가능 */
        }

        .bottom-button:active {
          transform: scale(0.92);
        }

        .cart-count {
          background-color: #ffffff;
          color: #fdcd27;
          width: 22px;
          height: 22px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          line-height: 1;
          padding: 0;
          box-sizing: border-box;
          position: relative;
          overflow: hidden;
        }
        
        .count-animation-container {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .count-digit {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
        
        .count-exit-up {
          animation: countSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .count-exit-down {
          animation: countSlideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .count-enter-up {
          animation: countSlideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .count-enter-down {
          animation: countSlideInDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes countSlideUp {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes countSlideDown {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes countSlideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes countSlideInDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* 룰렛 애니메이션 스타일 */
        .price-slot-container {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          overflow: hidden;
          height: 19px;
          position: relative;
          font-feature-settings: "tnum";
          font-variant-numeric: tabular-nums;
        }
        
        .digit-container {
          display: inline-flex;
          flex-direction: column;
          height: 19px;
          position: relative;
          overflow: hidden;
          min-width: 10px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .digit-container.comma {
          min-width: 4px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .digit-container.slot-appearing {
          animation: slotAppear 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .digit-container.slot-disappearing {
          animation: slotDisappear 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          min-width: 0;
          width: 0;
          margin: 0;
        }
        
        @keyframes slotAppear {
          0% {
            width: 0;
            min-width: 0;
            opacity: 0;
            margin: 0;
          }
          100% {
            width: 10px;
            min-width: 10px;
            opacity: 1;
            margin: 0;
          }
        }
        
        @keyframes slotDisappear {
          0% {
            width: 10px;
            min-width: 10px;
            opacity: 1;
            margin: 0;
          }
          100% {
            width: 0;
            min-width: 0;
            opacity: 0;
            margin: 0;
          }
        }
        
        .digit {
          display: inline-block;
          line-height: 19px;
          transition: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .digit-fade-in {
          animation: digitFadeIn 0.3s ease-out both;
        }
        
        .digit-exit {
          position: absolute;
          animation: slideOutBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-exit-up {
          position: absolute;
          animation: slideOutTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter {
          animation: slideInTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter-up {
          animation: slideInBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-intermediate {
          position: absolute;
          animation: slideIntermediateQuick 100ms linear both;
        }
        
        .digit-intermediate-up {
          position: absolute;
          animation: slideIntermediateQuickUp 100ms linear both;
        }
        
        @keyframes slideInTop {
          from {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutBottom {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideInBottom {
          from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutTop {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes slideIntermediateQuick {
          0% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0.8;
          }
          50% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideIntermediateQuickUp {
          0% {
            transform: translateX(-50%) translateY(100%);
            opacity: 0.8;
          }
          50% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes digitFadeIn {
          0% {
            opacity: 0;
            transform: translateX(-50%) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
        }
        
        .price-prefix {
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          line-height: 19px;
          margin-right: 1px;
        }
        
        .price-suffix {
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          line-height: 19px;
          margin-left: 2px;
        }

        .navigation-bar {
          width: 360px;
          height: 42px;
          background-image: url(${navigationBarImg});
          background-size: cover;
          background-position: center;
          position: fixed;
          bottom: 72px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 999;
        }
        
        .toast-popup {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #3C3C3C;
          color: #ffffff;
          padding: 0 16px;
          height: 42px;
          display: flex;
          align-items: center;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 600;
          z-index: 10000;
          white-space: nowrap;
          animation: toastSlideUp 0.3s ease-out, toastSlideDown 0.3s ease-out 1.7s forwards;
        }
        
        @keyframes toastSlideUp {
          from {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes toastSlideDown {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
        }
      `}</style>

      {/* 네이버 인앱 fallback 화면 (qr.js handoff 실패 시에만 도달, iOS 제외) */}
      {!shareMode
        && /naver/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')
        && !/iphone|ipad|ipod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')
        && (
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          height: '100vh', padding: '24px',
          textAlign: 'center', backgroundColor: '#F8FAFC'
        }}>
          <div style={{
            width: '40px', height: '40px', marginBottom: '20px',
            border: '3px solid #E5E7EB', borderTopColor: '#03C75A',
            borderRadius: '50%', animation: 'naverSpin 0.8s linear infinite'
          }} />
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1E293B', lineHeight: '1.5' }}>
            크롬 브라우저에서<br/>주문페이지가 열립니다
          </div>
          <div style={{ fontSize: '13px', color: '#94A3B8', marginTop: '8px' }}>
            잠시만 기다려주세요
          </div>
          <style>{`@keyframes naverSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* 공유 모드 화면 (관리자가 naver.me 생성용으로 ?share=qrCode 접근 시) */}
      {shareMode && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#F8FAFC'
        }}>
          <div style={{
            width: '96px',
            height: '96px',
            background: '#E0F2FE',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '28px'
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1E293B',
            marginBottom: '12px',
            letterSpacing: '-0.3px'
          }}>naver.me 공유 모드</div>
          <div style={{
            fontSize: '14px',
            color: '#64748B',
            lineHeight: '1.7',
            fontWeight: '400',
            marginBottom: '16px'
          }}>
            네이버 앱의 <strong>공유 버튼</strong>을 눌러<br/>
            naver.me 링크를 생성하세요.
          </div>
          <div style={{
            fontSize: '12px',
            color: '#94A3B8',
            padding: '8px 12px',
            background: '#F1F5F9',
            borderRadius: '6px',
            fontFamily: 'monospace'
          }}>
            QR Code: {shareMode.qrCode}
          </div>
        </div>
      )}

      {/* 세션 에러 화면 — iOS 네이버 인앱은 dispatch 안 하고 직접 렌더하므로 표시 대상 */}
      {!shareMode && !(/naver/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '') && !/iphone|ipad|ipod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')) && sessionError && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#F8FAFC'
        }}>
          <div style={{
            width: '96px',
            height: '96px',
            background: '#F1F5F9',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '28px'
          }}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1E293B',
            marginBottom: '12px',
            letterSpacing: '-0.3px',
            lineHeight: '1.4'
          }}>{tableOrder ? '주소 오류' : '세션 오류'}</div>
          <div style={{
            fontSize: '14px',
            color: '#94A3B8',
            lineHeight: '1.7',
            fontWeight: '400'
          }}>{sessionError}</div>
        </div>
      )}

      {/* 영업시간 외 차단 화면 — QR 스캔은 서버(qr.js)가 막고, 쿠키 세션 재진입은 여기서 막는다 */}
      {!shareMode && !sessionError && storeClosed && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#F8FAFC'
        }}>
          <div style={{
            width: '96px',
            height: '96px',
            background: '#F1F5F9',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '28px'
          }}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1E293B',
            marginBottom: '12px',
            letterSpacing: '-0.3px',
            lineHeight: '1.4'
          }}>주문 시간이 아닙니다</div>
          <div style={{
            fontSize: '14px',
            color: '#94A3B8',
            lineHeight: '1.7',
            fontWeight: '400'
          }}>
            {storeClosed.openTime && storeClosed.closeTime
              ? `영업시간 ${storeClosed.openTime} ~ ${storeClosed.closeTime} 에 주문하실 수 있습니다.`
              : '현재는 주문하실 수 없습니다.'}
            <br />매장 직원에게 문의해주세요.
          </div>
        </div>
      )}

      {/* 메인 콘텐츠 — iOS 네이버 인앱도 dispatch 안 하고 직접 렌더 (흰 화면 방지) */}
      {!shareMode && !sessionError && !storeClosed && !(/naver/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '') && !/iphone|ipad|ipod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')) && (
      isTabletLayout ? (
      <TabletOrderView
        storeName={storeInfo.storeName}
        tableNumber={tableNumber}
        categories={categories}
        categoryCodeMapping={categoryCodeMapping}
        categoryTranslations={categoryTranslations}
        activeCategoryIndex={tabletCategoryIndex}
        onSelectCategory={setTabletCategoryIndex}
        t={t}
        selectedLanguage={selectedLanguage}
        languages={languages}
        onSelectLanguage={setSelectedLanguage}
        menuData={menuData}
        translatedMenus={translatedMenus}
        menutagImages={menutagImages}
        calculateTimeRemaining={calculateTimeRemaining}
        onMenuClick={handleMenuClick}
        cartCount={cartCount}
        cartTotal={cartTotal}
        onOpenCart={() => setIsCartOpen(true)}
        onOpenOrderHistory={() => setIsOrderHistoryOpen(true)}
        onOpenStaffCall={() => setIsStaffCallOpen(true)}
        isToastVisible={isToastVisible}
        toastMessage={toastMessage}
      />
      ) : (
      <div className="scale-wrapper">
        {/* Top Container */}
        <div className="top-container">
          {/* Image Slider */}
          <div 
            className={`image-slider ${isDragging ? 'dragging' : ''}`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {!isLoadingImages && extendedImages.length > 0 && (
              <div
                className={`slider-wrapper ${isDragging || isTransitioning ? 'no-transition' : ''}`}
                style={{
                  transform: `translateX(${-currentSlide * 14.285714 + (isDragging ? (dragOffset / 360) * 14.285714 : 0)}%)`
                }}
              >
                {extendedImages.map((image, index) => {
                const realImageIndex = index === 0 ? sliderImages.length - 1 :
                                     index === extendedImages.length - 1 ? 0 :
                                     index - 1;
                const isError = imageLoadErrors[realImageIndex];

                if (isError) {
                  return (
                    <div key={`placeholder-${index}`} className="image-placeholder">
                      교촌치킨<br />이미지 {realImageIndex + 1}
                    </div>
                  );
                }

                if (isVideoUrl(image)) {
                  return (
                    <video
                      key={`extended-${index}`}
                      ref={el => {
                        videoRefs.current[index] = el;
                        // 마운트 직후 강제 즉시 로드 → Android Chrome의 lazy preload 우회
                        if (el && !el.dataset.eagerLoaded) {
                          el.dataset.eagerLoaded = '1';
                          try { el.load(); } catch (e) {}
                        }
                      }}
                      src={image}
                      className="slider-image"
                      muted
                      playsInline
                      preload="auto"
                      autoPlay={index === currentSlide}
                      draggable={false}
                      onEnded={() => {
                        // 활성 슬라이드의 비디오가 끝나면 다음 슬라이드로
                        if (currentSlideRef.current === index) {
                          nextSlide();
                        }
                      }}
                      onError={() => handleImageError(realImageIndex)}
                      onLoadedData={async (e) => {
                        handleImageLoad(realImageIndex);
                        // 비활성 비디오: 첫 프레임 강제 페인트를 위해 짧게 play→pause (currentTime=0 단독으론 모바일에서 미동작)
                        if (currentSlideRef.current !== index) {
                          const v = e.target;
                          try {
                            await v.play();
                            v.pause();
                            v.currentTime = 0;
                          } catch (err) {}
                        }
                      }}
                      style={{
                        objectFit: 'cover'
                      }}
                    />
                  );
                }

                return (
                  <img
                    key={`extended-${index}`}
                    src={image}
                    alt={`이미지 ${realImageIndex + 1}`}
                    className="slider-image"
                    draggable={false}
                    onError={() => handleImageError(realImageIndex)}
                    onLoad={() => handleImageLoad(realImageIndex)}
                    style={{
                      backgroundColor: '#f0f0f0'
                    }}
                  />
                );
                })}
              </div>
            )}
          </div>
          
          <div className="banner-pagination-dots">
            {sliderImages.map((_, index) => (
              <div
                key={index}
                className={`banner-dot ${realIndex === index ? 'active' : ''}`}
                onClick={() => {
                  goToSlide(index);
                  stopAutoPlay();
                  if (sliderImages.length > 1) {
                    setTimeout(() => startAutoPlay(), 2500);
                  }
                }}
              />
            ))}
          </div>
          
          <div
            className={`language-selector ${isLanguageExpanded ? 'expanded' : 'collapsed'}`}
            onClick={() => setIsLanguageExpanded(!isLanguageExpanded)}
          >
            <div className="language-options">
              {languages.map((lang) => (
                <div
                  key={lang.code}
                  className={`language-option ${selectedLanguage === lang.code ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    // 진동 피드백 추가
                    if (navigator.vibrate) {
                      navigator.vibrate(30);
                    }
                    setSelectedLanguage(lang.code);
                    setTimeout(() => setIsLanguageExpanded(false), 100);
                  }}
                >
                  <img src={lang.flag} alt={lang.name} className="language-flag" />
                  <span>{lang.name}</span>
                </div>
              ))}
            </div>
            <div className="language-icon" onClick={(e) => e.stopPropagation()}>
                <img src={languageIconImg} alt="언어" style={{ width: '19px', height: '19px' }} />
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="main-container">
          {/* Store Section - 매장 정보와 공지사항을 묶는 컨테이너 */}
          <div className="store-section">
            {/* Store Info Container */}
            <div className="store-info-container">
            <div className="store-info">
              <span className={`store-name${(storeInfo.storeName?.length || 0) >= 15 ? ' xlong' : (storeInfo.storeName?.length || 0) >= 10 ? ' long' : ''}`}>{storeInfo.storeName}</span>
              {tableNumber && (() => {
                const isSim = (() => { try { return sessionStorage.getItem('isSimulation') === 'true'; } catch (_) { return false; } })();
                return (
                  <div
                    className="table-badge"
                    onClick={isSim ? handleTableBadgeReset : undefined}
                    style={isSim ? { cursor: 'pointer' } : undefined}
                    title={isSim ? '주문내역 초기화' : undefined}
                  >
                    {t.tablePrefix}{tableNumber}{t.tableSuffix}
                  </div>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button className="action-btn" onClick={() => setIsStaffCallOpen(true)}>
                <img src={staffCallImg} alt={t.callStaff} />
                <span>{t.callStaff}</span>
              </button>
              
              <div className="live-status"
                   style={{
                     filter: reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko'
                       ? (reviewMark.channel === 'kakao'
                           ? 'drop-shadow(0px 0px 11px rgba(253, 205, 40, 0.28))'  // 카카오=관리자 리뷰설정 버튼과 동일 노랑 글로우
                           : 'drop-shadow(0px 0px 14px rgba(3, 207, 93, 0.15))')  // 15% opacity with 4px blur
                       : 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.1))',
                     cursor: reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko' ? 'pointer' : 'default'
                   }}
                   onClick={reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko' ? () => {
                     const isKakaoCh = reviewMark.channel === 'kakao';
                     const effPlaceId = isKakaoCh ? (reviewMark.kakaoPlaceId || '') : (reviewMark.placeId || '');
                     const reviewUrl = isKakaoCh
                       ? `https://place.map.kakao.com/${reviewMark.kakaoPlaceId}#addreview?rate=5`
                       : 'https://m.place.naver.com/my/checkin';
                     // sessionId 생성/재사용 (countdown 페이지와 동일 스킴)
                     let sid = null;
                     try {
                       sid = sessionStorage.getItem('sessionId');
                       if (!sid) {
                         sid = (window.crypto && window.crypto.randomUUID)
                           ? window.crypto.randomUUID()
                           : (Date.now().toString() + Math.random().toString(36).substr(2, 16));
                         sessionStorage.setItem('sessionId', sid);
                       }
                     } catch (e) { sid = Date.now().toString() + Math.random().toString(36).substr(2, 16); }
                     let cid = null;
                     try { cid = localStorage.getItem('clientId'); } catch (e) {}
                     // 리뷰 폴링 세션 시작 (keepalive로 페이지 이동 후에도 요청 보장)
                     try {
                       fetch('/api/review/start-review', {
                         method: 'POST',
                         headers: { 'Content-Type': 'application/json' },
                         keepalive: true,
                         body: JSON.stringify({
                           placeId: effPlaceId,
                           sessionId: sid,
                           clientId: cid,
                           storeId: sessionData?.store_id || '',
                           tableCode: sessionData?.table_code || '',
                           sessionToken: sessionToken || '',
                           channel: reviewMark.channel || 'naver'
                         })
                       }).catch(() => {});
                     } catch (e) {}
                     window.location.href = reviewUrl;
                   } : undefined}
              >
                <svg width="180" height="64" viewBox="-1 -1 180 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* 흰색 배경 (그림자가 내부로 침투하지 않도록) */}
                  <path d="M0 31C0 16.8854 0 9.82812 4.14478 5.25506C4.49648 4.86702 4.86702 4.49648 5.25506 4.14478C9.82812 0 16.8854 0 31 0H147C161.115 0 168.172 0 172.745 4.14478C173.133 4.49648 173.504 4.86702 173.855 5.25506C178 9.82812 178 16.8854 178 31V31C178 45.1146 178 52.1719 173.855 56.7449C173.504 57.133 173.133 57.5035 172.745 57.8552C168.172 62 161.115 62 147 62H31C16.8854 62 9.82812 62 5.25506 57.8552C4.86702 57.5035 4.49648 57.133 4.14478 56.7449C0 52.1719 0 45.1146 0 31V31Z" 
                        fill="#ffffff"/>
                  
                  {/* 내부 배경 (옅은 그라데이션) */}
                  <path d="M0 31C0 16.8854 0 9.82812 4.14478 5.25506C4.49648 4.86702 4.86702 4.49648 5.25506 4.14478C9.82812 0 16.8854 0 31 0H147C161.115 0 168.172 0 172.745 4.14478C173.133 4.49648 173.504 4.86702 173.855 5.25506C178 9.82812 178 16.8854 178 31V31C178 45.1146 178 52.1719 173.855 56.7449C173.504 57.133 173.133 57.5035 172.745 57.8552C168.172 62 161.115 62 147 62H31C16.8854 62 9.82812 62 5.25506 57.8552C4.86702 57.5035 4.49648 57.133 4.14478 56.7449C0 52.1719 0 45.1146 0 31V31Z" 
                        fill="url(#inner_gradient_small)"/>
                  
                  {/* 외곽선 (진한 그라데이션) - 바깥쪽에 그리기 */}
                  <path d="M0 31C0 16.8854 0 9.82812 4.14478 5.25506C4.49648 4.86702 4.86702 4.49648 5.25506 4.14478C9.82812 0 16.8854 0 31 0H147C161.115 0 168.172 0 172.745 4.14478C173.133 4.49648 173.504 4.86702 173.855 5.25506C178 9.82812 178 16.8854 178 31V31C178 45.1146 178 52.1719 173.855 56.7449C173.504 57.133 173.133 57.5035 172.745 57.8552C168.172 62 161.115 62 147 62H31C16.8854 62 9.82812 62 5.25506 57.8552C4.86702 57.5035 4.49648 57.133 4.14478 56.7449C0 52.1719 0 45.1146 0 31V31Z" 
                        fill="none"
                        stroke="url(#border_gradient_small)"
                        strokeWidth="1"/>
                  
                  <defs>
                    
                    {/* 내부 배경용 그라데이션 - 리뷰 주문 여부 및 언어에 따라 다른 색상 */}
                    <linearGradient id="inner_gradient_small" x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
                      {reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko' ? (
                        reviewMark.channel === 'kakao' ? (
                          <>
                            <stop offset="0%" stopColor="#FDE68A" stopOpacity="0.18"/>
                            <stop offset="50%" stopColor="#FDCD28" stopOpacity="0.18"/>
                            <stop offset="100%" stopColor="#F5B800" stopOpacity="0.18"/>
                          </>
                        ) : (
                          <>
                            <stop offset="0%" stopColor="#66CF03" stopOpacity="0.1"/>
                            <stop offset="50%" stopColor="#03CF5D" stopOpacity="0.1"/>
                            <stop offset="100%" stopColor="#03CFA3" stopOpacity="0.1"/>
                          </>
                        )
                      ) : (
                        <>
                          <stop offset="0%" stopColor="#fcd34d" stopOpacity="0.15">
                            <animate attributeName="stop-color"
                                values="#fcd34d;#fb923c;#c084fc;#fcd34d"
                                dur="3s"
                                repeatCount="indefinite"/>
                          </stop>
                          <stop offset="50%" stopColor="#c084fc" stopOpacity="0.1">
                            <animate attributeName="stop-color"
                                values="#c084fc;#fcd34d;#fb923c;#c084fc"
                                dur="3s"
                                repeatCount="indefinite"/>
                          </stop>
                          <stop offset="100%" stopColor="#fb923c" stopOpacity="0.15">
                            <animate attributeName="stop-color"
                                values="#fb923c;#c084fc;#fcd34d;#fb923c"
                                dur="3s"
                                repeatCount="indefinite"/>
                          </stop>
                        </>
                      )}
                    </linearGradient>
                    
                    {/* 외곽선용 그라데이션 - 리뷰 주문 여부 및 언어에 따라 다른 색상 */}
                    <linearGradient id="border_gradient_small" x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
                      {reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko' ? (
                        reviewMark.channel === 'kakao' ? (
                          <>
                            <stop offset="0%" stopColor="#FDE68A" stopOpacity="1"/>
                            <stop offset="50%" stopColor="#FDCD28" stopOpacity="1"/>
                            <stop offset="100%" stopColor="#F5B800" stopOpacity="1"/>
                          </>
                        ) : (
                          <>
                            <stop offset="0%" stopColor="#66CF03" stopOpacity="1"/>
                            <stop offset="50%" stopColor="#03CF5D" stopOpacity="1"/>
                            <stop offset="100%" stopColor="#03CFA3" stopOpacity="1"/>
                          </>
                        )
                      ) : (
                        <>
                          <stop offset="0%" stopColor="#fcd34d" stopOpacity="1">
                            <animate attributeName="stop-color" 
                                values="#fcd34d;#fb923c;#c084fc;#fcd34d" 
                                dur="3s" 
                                repeatCount="indefinite"/>
                          </stop>
                          <stop offset="50%" stopColor="#c084fc" stopOpacity="1">
                            <animate attributeName="stop-color" 
                                values="#c084fc;#fcd34d;#fb923c;#c084fc" 
                                dur="3s" 
                                repeatCount="indefinite"/>
                          </stop>
                          <stop offset="100%" stopColor="#fb923c" stopOpacity="1">
                            <animate attributeName="stop-color" 
                                values="#fb923c;#c084fc;#fcd34d;#fb923c" 
                                dur="3s" 
                                repeatCount="indefinite"/>
                          </stop>
                        </>
                      )}
                    </linearGradient>
                  </defs>
                </svg>
                <div className="live-status-content" style={{ marginTop: reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko' ? '3px' : '0' }}>
                  {reviewMark.marked && reviewSettings.isEnabled && selectedLanguage === 'ko' ? (
                    <>
                      {reviewMark.channel === 'kakao' ? (
                        <svg viewBox="0 0 450 450" xmlns="http://www.w3.org/2000/svg" aria-label="카카오" style={{ width: '23px', height: '23px', marginBottom: '5px' }}>
                          <path fill="#fae000" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M220.265 496.122c-34.176 0-61.88 27.703-61.88 61.866v312.815c0 34.176 27.704 61.879 61.88 61.879h312.814c34.176 0 61.867-27.703 61.867-61.88V557.989c0-34.163-27.69-61.866-61.867-61.866z" />
                          <path fill="#007dfc" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M378.298 552.754c-63.196-.038-114.244 50.744-114.333 113.586-.05 28.5 11.666 52.834 29.312 73.596 32.187 37.849 60.359 77.712 75.736 125.556 1.381 4.307 3.459 9.222 9.4 9.07 6.46-.165 8.195-5.662 9.728-10.311 14.428-43.778 38.546-81.59 69.01-115.942 18.102-20.407 32.63-43.93 35.063-71.57 5.94-67.338-47.312-124.91-113.916-123.985m38.863 114.662c-.152 20.8-18.456 38.99-39.04 38.8-20.774-.19-38.8-18.444-38.723-39.167.076-20.71 18.392-38.875 39.09-38.774 20.673.089 38.825 18.469 38.673 39.141" />
                        </svg>
                      ) : (
                        <img src={liveStatusNaverImg}
                             alt="네이버 리뷰"
                             style={{ width: '23px', height: '23px', marginBottom: '5px' }}/>
                      )}
                      <span style={{ fontSize: '10px', fontWeight: '700', color: reviewMark.channel === 'kakao' ? '#C79200' : '#1CB15E', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {reviewMark.channel === 'kakao' ? '카카오' : '네이버'} 리뷰 쓰러 가기
                        <svg width="5" height="8" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', marginBottom: '2px' }}>
                          <path fillRule="evenodd" clipRule="evenodd" d="M4.34729 4.12183C4.44507 4.22042 4.5 4.35413 4.5 4.49354C4.5 4.63295 4.44507 4.76666 4.34729 4.86525L1.39674 7.83949C1.34862 7.88971 1.29107 7.92976 1.22744 7.95731C1.1638 7.98487 1.09536 7.99937 1.02611 7.99998C0.956853 8.00059 0.888173 7.98728 0.824073 7.96085C0.759973 7.93441 0.701738 7.89537 0.652766 7.84601C0.603794 7.79664 0.565065 7.73794 0.53884 7.67333C0.512615 7.60871 0.499418 7.53948 0.50002 7.46967C0.500621 7.39986 0.51501 7.33087 0.542345 7.26672C0.56968 7.20258 0.609415 7.14456 0.659231 7.09606L3.24103 4.49354L0.65923 1.89101C0.564221 1.79186 0.511649 1.65905 0.512838 1.52119C0.514026 1.38334 0.56888 1.25147 0.665584 1.15399C0.762288 1.05651 0.893106 1.00122 1.02986 1.00002C1.16662 0.998822 1.29837 1.05182 1.39674 1.14759L4.34729 4.12183Z" fill={reviewMark.channel === 'kakao' ? '#C79200' : '#1CB15E'}/>
                        </svg>
                      </span>
                    </>
                  ) : (
                    <>
                      <img src={liveStatusImg} alt="실시간" />
                      <svg width="150" height="14" style={{ overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="text_gradient_static" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9CE34" stopOpacity="1"/>
                        <stop offset="50%" stopColor="#EE2A7B" stopOpacity="0.8"/>
                        <stop offset="100%" stopColor="#6228D7" stopOpacity="1"/>
                      </linearGradient>
                    </defs>
                    <text x="50%" y="50%"
                          dominantBaseline="middle" 
                          textAnchor="middle"
                          fill="url(#text_gradient_static)"
                          fontSize="10"
                          fontWeight="700"
                          fontFamily="'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', sans-serif">
                      {activeUsers === 1 ? `1${t.orderingAlone}` : `${activeUsers}${t.orderingTogether}`}
                    </text>
                  </svg>
                    </>
                  )}
                </div>
              </div>
              
              <button className="action-btn" onClick={() => setIsOrderHistoryOpen(true)}>
                <img src={orderHistoryImg} alt={t.orders} />
                <span>{t.orders}</span>
              </button>
            </div>
          </div>

            {/* Notice */}
            {noticeSettings.enabled && (
              <div className="notice-container">
                <div
                  ref={noticeRef}
                  className={`notice ${isNoticeExpanded ? 'expanded' : ''}`}
                  onClick={() => noticeNeedsExpand && setIsNoticeExpanded(!isNoticeExpanded)}
                  style={{ cursor: noticeNeedsExpand ? 'pointer' : 'default' }}
                >
                  <div className="notice-content" ref={noticeContentRef}>

                    <img className="notice-icon" src={noticeIconImg} alt="공지" style={{ width: '14px', height: '14px' }} />

                    <div className="notice-text">
                      {noticeSettings.texts?.[selectedLanguage] || ''}
                    </div>
                  </div>
                  {noticeNeedsExpand && <div className="notice-expand-btn"></div>}
                </div>
              </div>
            )}
          </div>


          {/* Category Container — 검색 모드 시 카테고리 바 자리에 검색 인풋 표시 */}
          <div className="category-container" ref={tabsRef}>
            {!isSearchOpen ? (
              <>
                <div className="category-tabs" ref={tabContainerRef}>
                  {categories.map((category, index) => (
                    <div
                      key={index}
                      ref={el => tabRefs.current[index] = el}
                      className={`category-tab ${activeTab === index ? 'active' : ''}`}
                      onClick={() => handleTabClick(index)}
                    >
                      {(() => {
                        const categoryCode = categoryCodeMapping[category];
                        return (categoryCode && categoryTranslations[categoryCode]?.[selectedLanguage]) || category;
                      })()}
                    </div>
                  ))}
                  <div
                    className="category-highlight"
                    style={highlightStyle}
                  />
                </div>
                <button
                  className="category-search-btn"
                  onClick={() => { setIsSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                  aria-label={searchPlaceholder}
                  type="button"
                >
                  <Icon icon="material-symbols:search-rounded" width={22} height={22} color="#000000" />
                </button>
              </>
            ) : (
              <div className="search-bar">
                <Icon icon="material-symbols:search-rounded" width={20} height={20} color="#797979" />
                <input
                  ref={searchInputRef}
                  className="search-input"
                  type="text"
                  inputMode="search"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <button
                  className="search-close-btn"
                  onClick={closeSearch}
                  aria-label="close"
                  type="button"
                >
                  <Icon icon="material-symbols:close-rounded" width={22} height={22} color="#000000" />
                </button>
              </div>
            )}
          </div>

          {/* Menu Containers — 검색 모드 시 검색 결과를 카테고리별 그룹화해서 표시 */}
          <div className="menu-display-container">
            {(() => {
              const searchResults = isSearchOpen ? getSearchResults() : null;
              if (isSearchOpen && searchResults === null) {
                return (
                  <div className="search-state-message">{searchEmptyText}</div>
                );
              }
              if (isSearchOpen && Object.keys(searchResults).length === 0) {
                return (
                  <div className="search-state-message">{searchNoResultsText}</div>
                );
              }
              const displayCategories = isSearchOpen ? Object.keys(searchResults) : categories;
              const displaySource = isSearchOpen ? searchResults : (menuData || defaultMenuData);
              return displayCategories.map((category, categoryIndex) => {
              const categoryItems = displaySource?.[category] || [];

              // 메뉴가 없는 카테고리는 렌더링하지 않음
              if (categoryItems.length === 0) return null;

              return (
              <div
                key={category}
                className="menu-container"
                ref={isSearchOpen ? null : (el => sectionRefs.current[categoryIndex] = el)}
              >
                <div
                  className="category-badge-container"
                  ref={isSearchOpen ? null : (el => categoryHeaderRefs.current[categoryIndex] = el)}
                >
                  <div className="category-badge">{translatedCategories[category] || category}</div>
                </div>
                {categoryItems.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    data-menu-id={item.id}
                    className={`menu-item ${(item.soldOut || item.is_soldout) ? 'sold-out' : ''} ${calculateTimeRemaining(item) ? 'time-limited' : ''} ${!(item.image_url || item.image) ? 'no-image' : ''}`}
                    onClick={!(item.soldOut || item.is_soldout) && !calculateTimeRemaining(item) ? (e) => {
                      // 스크롤 중이 아닐 때만 클릭 처리
                      if (!isScrolling) {
                        createRipple(e.currentTarget, e);
                        handleMenuClick(item);
                      }
                    } : undefined}
                  >
                    <div 
                      className="menu-item-basic"
                    >
                      <div className="menu-info">
                        <div className="menu-tags">
                          {(() => {
                            const tags = [];
                            if (item.is_popular || item.tags?.includes('popular')) tags.push('popular');
                            if (item.is_recommend || item.tags?.includes('recommend')) tags.push('recommend');
                            if (item.is_new || item.tags?.includes('new')) tags.push('new');
                            if (item.is_most_ordered) tags.push('mostOrdered');

                            return tags.sort((a, b) => {
                              const order = { 'popular': 0, 'recommend': 1, 'new': 2, 'mostOrdered': 3 };
                              return order[a] - order[b];
                            }).map((tag, tagIndex) => {
                              if (tag === 'mostOrdered') {
                                const mostOrderedLabel = {
                                  ko: '많이 골랐어요!',
                                  en: 'Most Ordered!',
                                  ja: '人気No.1!',
                                  zh: '人气推荐!',
                                }[selectedLanguage] || '많이 골랐어요!';
                                return (
                                  <span key={tagIndex} className="menu-tag-most-ordered">{mostOrderedLabel}</span>
                                );
                              }

                              const getTagImageUrl = () => {
                                const langPath = selectedLanguage === 'en' ? 'english' : selectedLanguage === 'ja' ? 'japan' : selectedLanguage === 'zh' ? 'china' : 'korean';
                                const tagType = tag === 'popular' ? 'popular' : tag === 'recommend' ? 'recommend' : 'new';
                                return menutagImages[`${langPath}/${tagType}`];
                              };

                              return (
                                <img
                                  key={tagIndex}
                                  className="menu-tag"
                                  src={getTagImageUrl()}
                                  alt={tag}
                                />
                              );
                            });
                          })()}
                        </div>
                        <div className="menu-name">{translatedMenus[item.itemCode]?.name || item[`name_${selectedLanguage}`] || item.name}</div>
                        <div className="menu-description">{translatedMenus[item.itemCode]?.description || item[`description_${selectedLanguage}`] || item.description || ''}</div>
                        <div className="menu-price">
                          {(() => {
                            const raw = item.basePrice || item.price;
                            const num = typeof raw === 'number'
                              ? raw
                              : parseInt(String(raw || '0').replace(/[^0-9]/g, '')) || 0;
                            return selectedLanguage === 'ko'
                              ? `${num.toLocaleString()}원`
                              : `₩${num.toLocaleString()}`;
                          })()}
                        </div>
                      </div>
                      <div className={`menu-image-container ${!(item.image_url || item.image) ? 'no-image' : ''}`}>
                        {(item.image_url || item.image) && (
                          <img
                            className="menu-image"
                            src={item.image_url || item.image}
                            alt={translatedMenus[item.itemCode]?.name || item[`name_${selectedLanguage}`] || item.name}
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        {(item.soldOut || item.is_soldout) && !calculateTimeRemaining(item) && (
                          <div className="sold-out-overlay">
                            <div className="sold-out-badge">
                              {selectedLanguage === 'ko' ? '품절' :
                               selectedLanguage === 'en' ? 'Sold out' :
                               selectedLanguage === 'ja' ? '売切れ' :
                               '售完'}
                            </div>
                          </div>
                        )}
                        {(() => {
                          // 판매시간 우선 — 판매시간 밖이면 품절이어도 오픈 시간 배지를 먼저 표시
                          const timeInfo = calculateTimeRemaining(item);
                          if (timeInfo) {
                            return (
                              <div className="sold-out-overlay">
                                <div className="time-limit-badge">
                                  {(() => {
                                    if (timeInfo.hours !== null) {
                                      // 1시간 이상 남았을 때
                                      return selectedLanguage === 'ko' 
                                        ? `${timeInfo.hours}시간 후 오픈`
                                        : selectedLanguage === 'en'
                                        ? `Opens in ${timeInfo.hours}h`
                                        : selectedLanguage === 'ja'
                                        ? `${timeInfo.hours}時間後オープン`
                                        : `${timeInfo.hours}小时后开放`;
                                    } else if (timeInfo.minutes !== null) {
                                      // 1시간 미만 남았을 때
                                      return selectedLanguage === 'ko' 
                                        ? `${timeInfo.minutes}분 후 오픈`
                                        : selectedLanguage === 'en'
                                        ? `Opens in ${timeInfo.minutes}m`
                                        : selectedLanguage === 'ja'
                                        ? `${timeInfo.minutes}分後オープン`
                                        : `${timeInfo.minutes}分钟后开放`;
                                    }
                                  })()}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {!(item.soldOut || item.is_soldout) && !calculateTimeRemaining(item) && (
                          <button className="quick-add-btn" onClick={(e) => handleQuickAdd(item, e)}>
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect y="5.77783" width="13" height="1.44444" rx="0.722222" fill="black"/>
                              <rect x="7.22217" width="13" height="1.44444" rx="0.722222" transform="rotate(90 7.22217 0)" fill="black"/>
                            </svg>
                          </button>
                        )}
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            )});
            })()}
          </div>
        </div>

        {/* White Spacer Box */}
        <div className="white-spacer"></div>

        {/* Bottom Info */}
        <div className="bottom-info">
          <div className="info-section">
            <div className="info-title">{t.businessInfo}</div>
            <div className="info-content">
              {(!storeInfo.businessName && !storeInfo.address && !storeInfo.phoneNumber && !storeInfo.businessNumber) ? (
                '사업자 정보가 없습니다'
              ) : (
                <>
                  {storeInfo.businessName && <>상호명 : {storeInfo.businessName}<br /></>}
                  {storeInfo.address && <>주소 : {storeInfo.address}<br /></>}
                  {storeInfo.phoneNumber && <>전화번호 : {storeInfo.phoneNumber}<br /></>}
                  {storeInfo.businessNumber && <>사업자등록번호 : {storeInfo.businessNumber}</>}
                </>
              )}
            </div>
          </div>

          <div className="info-section">
            <div className="info-title">{t.originInfo}</div>
            <div className="info-content">
              {storeInfo.origins || '원산지 정보가 없습니다'}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Button — 카트 드로어가 열려 있으면 렌더 안 함(뒤 버튼으로 클릭 새는 것 방지) */}
        {cartCount > 0 && displayPrice !== null && displayCount !== null && !isCartOpen && (
          <div className="fixed-bottom">
            <button className="bottom-button" {...tapClickProps} onClick={() => setIsCartOpen(true)}>
              <div className="cart-count">
                {countAnimating ? (
                  <div className="count-animation-container">
                    <span 
                      className={`count-digit ${isCountIncreasing ? 'count-exit-up' : 'count-exit-down'}`}
                      key={`prev-${countAnimationKey}`}
                    >
                      {prevCount}
                    </span>
                    <span 
                      className={`count-digit ${isCountIncreasing ? 'count-enter-up' : 'count-enter-down'}`}
                      key={`current-${countAnimationKey}`}
                    >
                      {displayCount}
                    </span>
                  </div>
                ) : (
                  <span className="count-digit">{displayCount}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
              {selectedLanguage !== 'en' && <span style={{ marginRight: '4px' }}>{t.cart}</span>}
                <div className="price-slot-container">
                {/* 해외 언어일 때 ₩ 표시 */}
                {selectedLanguage !== 'ko' && (
                  <span className="price-prefix">₩</span>
                )}
                {(() => {
                  // 가격 포맷팅 함수
                  const formatPriceForAnimation = (price) => {
                    if (!price) return [];
                    const numStr = price.toString().replace(/,/g, '');
                    const result = [];
                    for (let i = 0; i < numStr.length; i++) {
                      if (i > 0 && (numStr.length - i) % 3 === 0) {
                        result.push(',');
                      }
                      result.push(numStr[i]);
                    }
                    return result;
                  };
                  
                  const currentPriceArray = formatPriceForAnimation(displayPrice);
                  const prevPriceArray = formatPriceForAnimation(prevPrice);
                  
                  // 더 긴 길이에 맞춰 정렬
                  const maxLength = Math.max(currentPriceArray.length, prevPriceArray.length);
                  const alignedCurrent = Array(maxLength).fill(' ');
                  const alignedPrev = Array(maxLength).fill(' ');
                  
                  // 뒤에서부터 채우기
                  for (let i = 0; i < currentPriceArray.length; i++) {
                    alignedCurrent[maxLength - currentPriceArray.length + i] = currentPriceArray[i];
                  }
                  for (let i = 0; i < prevPriceArray.length; i++) {
                    alignedPrev[maxLength - prevPriceArray.length + i] = prevPriceArray[i];
                  }
                  
                  return alignedCurrent.map((digit, index) => {
                    const prevDigit = alignedPrev[index];
                    const isNewSlot = prevDigit === ' ' && digit !== ' ';
                    const isRemovedSlot = prevDigit !== ' ' && digit === ' ';
                    const isChanged = priceAnimating && prevDigit !== digit && !isNewSlot && !isRemovedSlot;
                    
                    // 쉼표는 애니메이션 없이 처리
                    if (digit === ',') {
                      return (
                        <div key={`${index}-${animationKey}-comma`} className="digit-container comma">
                          <span className={`digit ${isNewSlot ? 'digit-fade-in' : ''}`}>,</span>
                        </div>
                      );
                    }
                    
                    // 빈 공간은 렌더링하지 않음
                    if (digit === ' ' && !isNewSlot && !priceAnimating) {
                      return null;
                    }
                    
                    // 중간 숫자 생성
                    const generateSmoothIntermediates = () => {
                      if (!isChanged || isNaN(prevDigit) || isNaN(digit) || prevDigit === ' ' || digit === ' ') {
                        return [];
                      }
                      
                      const from = parseInt(prevDigit);
                      const to = parseInt(digit);
                      const diff = Math.abs(to - from);
                      
                      if (diff >= 5) {
                        const intermediates = [];
                        if (isPriceIncreasing) {
                          for (let i = 0; i < 3; i++) {
                            intermediates.push((from + i + 1) % 10);
                          }
                        } else {
                          for (let i = 0; i < 3; i++) {
                            intermediates.push((from - i - 1 + 10) % 10);
                          }
                        }
                        return intermediates;
                      }
                      
                      const intermediates = [];
                      if (from < to) {
                        for (let i = from + 1; i < to; i++) {
                          intermediates.push(i);
                        }
                      } else {
                        for (let i = from - 1; i > to; i--) {
                          intermediates.push(i);
                        }
                      }
                      return intermediates;
                    };
                    
                    const intermediateDigits = generateSmoothIntermediates();
                    const animationDelay = index * 30;
                    
                    return (
                      <div key={`${index}-${animationKey}`} className={`digit-container ${isNewSlot ? 'slot-appearing' : ''} ${isRemovedSlot ? 'slot-disappearing' : ''}`}>
                        {isChanged && prevDigit !== ' ' && (
                          <span 
                            className={`digit ${isPriceIncreasing ? 'digit-exit-up' : 'digit-exit'}`}
                            style={{ animationDelay: `${animationDelay}ms` }}
                          >
                            {prevDigit}
                          </span>
                        )}
                        
                        {intermediateDigits.map((intermediateDigit, i) => (
                          <span
                            key={`intermediate-${i}`}
                            className={`digit ${isPriceIncreasing ? 'digit-intermediate-up' : 'digit-intermediate'}`}
                            style={{ 
                              animationDelay: `${animationDelay + (i + 1) * 50}ms`,
                              animationDuration: '50ms'
                            }}
                          >
                            {intermediateDigit}
                          </span>
                        ))}
                        
                        {digit !== ' ' && (
                          <span 
                            className={`digit ${isChanged ? (isPriceIncreasing ? 'digit-enter-up' : 'digit-enter') : ''} ${isNewSlot ? 'digit-fade-in' : ''}`}
                            style={isChanged ? { animationDelay: `${animationDelay + (intermediateDigits.length * 50) + 50}ms` } : isNewSlot ? { animationDelay: `${animationDelay}ms` } : {}}
                          >
                            {digit}
                          </span>
                        )}
                      </div>
                    );
                  }).filter(Boolean);
                })()}
                {/* 한국어일 때 원 표시 */}
                {selectedLanguage === 'ko' && (
                  <span className="price-suffix">원</span>
                )}
                </div>
                {/* 영어일 때 in cart 표시 */}
                {selectedLanguage === 'en' && (
                  <span style={{ marginLeft: '4px' }}>{t.inCart}</span>
                )}
              </div>
            </button>
          </div>
        )}
        
        {/* 토스트 팝업 */}
        {isToastVisible && (
          <div className="toast-popup" dangerouslySetInnerHTML={{ __html: toastMessage }} />
        )}
      </div>
      ))}

      {/* 메뉴 옵션 바텀시트 */}
      <MenuOptionsBottomSheet 
        isOpen={isBottomSheetOpen}
        onClose={handleBottomSheetClose}
        menuItem={selectedMenuItem}
        selectedLanguage={selectedLanguage}
        onAddToCart={() => {
          const checkIcon = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>';
          showToast(
            selectedLanguage === 'ko' ? checkIcon + '메뉴를 담았어요!' :
            selectedLanguage === 'en' ? checkIcon + 'Menu added!' :
            selectedLanguage === 'ja' ? checkIcon + '追加しました！' :
            checkIcon + '已加入购物车！'
          );

          // WebSocket 동기화는 menuOptionsBottomSheet에서 addCartItem()으로 처리됨
          // (중복 호출 제거 - deprecated 메서드 사용하지 않음)

          loadCartData(); // 장바구니 데이터 새로고침
        }}
      />
      
      {/* 장바구니 화면 */}
      <CartDrawer
        isOpen={isCartOpen}
        selectedLanguage={selectedLanguage}
        tableCode={sessionData?.table_code || '1'}
        posType={sessionData?.pos_type || null}
        storeId={sessionData?.store_id || null}
        isSimulation={sessionStorage.getItem('isSimulation') === 'true'}
        sessionToken={sessionToken}
        reviewSettings={reviewSettings}
        hasExistingOrder={hasExistingOrder}
        onClose={() => {
          setIsCartOpen(false);
          loadCartData(); // 장바구니 닫을 때 데이터 새로고침
        }}
        onOrderComplete={() => {
          // 주문 완료 후 주문내역 페이지 열기 (애니메이션 없이)
          setSkipOrderHistoryAnimation(true);
          setIsOrderHistoryOpen(true);
        }}
        onShowToast={showToast}
        onRequestScrollToCategories={() => {
          // 스크롤을 먼저 시작
          const firstSection = sectionRefs.current[0];
          if (firstSection) {
            const rect = firstSection.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = rect.top + scrollTop - 51;
            
            // 빠른 스크롤 애니메이션
            const startPosition = window.pageYOffset;
            const distance = targetPosition - startPosition;
            const duration = 300;
            let start = null;
            
            const animation = (currentTime) => {
              if (start === null) start = currentTime;
              const timeElapsed = currentTime - start;
              const progress = Math.min(timeElapsed / duration, 1);
              
              const easeOutCubic = 1 - Math.pow(1 - progress, 3);
              
              window.scrollTo(0, startPosition + distance * easeOutCubic);
              
              if (timeElapsed < duration) {
                requestAnimationFrame(animation);
              }
            };
            
            requestAnimationFrame(animation);
          }
        }}
      />
      
      {/* 주문 내역 화면 */}
      <OrderHistory
        isOpen={isOrderHistoryOpen}
        selectedLanguage={selectedLanguage}
        tableCode={sessionData?.table_code || '1'}
        tableName={sessionData?.table_name || null}
        storeId={sessionData?.store_id || null}
        posType={sessionData?.pos_type || null}
        isSimulation={sessionStorage.getItem('isSimulation') === 'true'}
        onClose={(shouldScroll) => {
          setIsOrderHistoryOpen(false);
          setSkipOrderHistoryAnimation(false);
          
          // 주문하기 버튼으로 닫힌 경우에만 스크롤
          if (shouldScroll) {
            // 페이지가 완전히 닫힌 후 스크롤 시작
            setTimeout(() => {
              const firstSection = sectionRefs.current[0];
              if (firstSection) {
                const rect = firstSection.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const targetPosition = rect.top + scrollTop - 51;
                
                // 스크롤 애니메이션
                const startPosition = window.pageYOffset;
                const distance = targetPosition - startPosition;
                const duration = 400;
                let start = null;
                
                const animation = (currentTime) => {
                  if (start === null) start = currentTime;
                  const timeElapsed = currentTime - start;
                  const progress = Math.min(timeElapsed / duration, 1);
                  
                  const easeOutCubic = 1 - Math.pow(1 - progress, 3);
                  
                  window.scrollTo(0, startPosition + distance * easeOutCubic);
                  
                  if (timeElapsed < duration) {
                    requestAnimationFrame(animation);
                  }
                };
                
                requestAnimationFrame(animation);
              }
            }, 150); // 페이지 닫기 애니메이션(150ms) 완료 후
          }
        }}
        skipAnimation={skipOrderHistoryAnimation}
      />
      
      {/* 직원호출 화면 */}
      <StaffCallBottomSheet
        isOpen={isStaffCallOpen}
        onClose={() => setIsStaffCallOpen(false)}
        selectedLanguage={selectedLanguage}
        tableCode={sessionData?.table_code || '1'}
        storeId={sessionData?.store_id || null}
        posType={sessionData?.pos_type || null}
        isSimulation={sessionStorage.getItem('isSimulation') === 'true'}
        onCall={() => {
          const checkIcon = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>';
          showToast(
            selectedLanguage === 'ko' ? checkIcon + '직원을 호출했어요!' :
            selectedLanguage === 'en' ? checkIcon + 'Staff called!' :
            selectedLanguage === 'ja' ? checkIcon + 'スタッフを呼出しました！' :
            checkIcon + '已呼叫服务员！'
          );
        }}
      />
    </>
  );
};

export default OrderPage;